import React, { useState, useEffect, useMemo } from "react";
import {
  Wallet, PiggyBank, CreditCard, TrendingUp, Plus, X, Eye, EyeOff,
  AlertTriangle, ArrowUpRight, ArrowDownRight, Repeat, Landmark, Target, Settings,
  LayoutGrid, ListTree, Building2, Trash2, Check, Lock, Cherry, CalendarClock,
  Menu, Bell, SlidersHorizontal, BarChart3, PieChartIcon, LogOut, RefreshCw,
  Users, ShieldCheck, History
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip, CartesianGrid
} from "recharts";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { db, auth } from "./firebase";
import AuthScreen from "./AuthScreen";

/* ============================== CONSTANTES ============================== */

const STORAGE_KEY = "ppcacao:data";

const GOLD = "#C9A15A";
const GOLD_LIGHT = "#E7C888";
const BG = "#0E0B08";
const PANEL = "#1D1712";
const PANEL_2 = "#2A2118";
const BORDER = "#3D3122";
const GREEN = "#5FA772";
const YELLOW = "#D9A441";
const RED = "#C15B4A";
const TEXT_MUTED = "#B3A48D";
const APP_VERSION = "V1";
const APP_AUTHOR = "Jose Fernando Dominguez Dominguez";
const LOGO_DATA_URI = "data:image/png;base64,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";
const LOGO_LIGHT_DATA_URI = "data:image/png;base64,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";

const BOLSA_COLORS = ["#C9A15A", "#5FA772", "#6E8FAF", "#B06C4C", "#8F76B0", "#C15B4A", "#4E9E9A", "#B08F3E"];

const TIPOS_MOVIMIENTO = [
  { id: "ingreso", label: "Ingreso", icon: ArrowDownRight, tone: GREEN },
  { id: "gasto", label: "Gasto", icon: ArrowUpRight, tone: RED },
  { id: "transferencia_bolsas", label: "Mover entre bolsas", icon: Repeat, tone: GOLD },
  { id: "pago_deuda", label: "Pago de deuda", icon: Landmark, tone: YELLOW },
  { id: "prestamo_otorgado", label: "Prestar dinero", icon: Users, tone: YELLOW },
  { id: "cobro_prestamo", label: "Cobro de préstamo", icon: ArrowDownRight, tone: GREEN },
  { id: "rendimiento", label: "Rendimiento", icon: TrendingUp, tone: GOLD_LIGHT },
  { id: "ajuste", label: "Ajuste / conciliación", icon: RefreshCw, tone: TEXT_MUTED },
  { id: "trabajo_ingreso", label: "Entrada (cuenta de trabajo)", icon: ArrowDownRight, tone: GREEN },
  { id: "trabajo_gasto", label: "Salida (cuenta de trabajo)", icon: ArrowUpRight, tone: RED },
];

const CUENTA_CLASES = [
  { id: "patrimonio", label: "Patrimonio" },
  { id: "trabajo", label: "Trabajo (solo control)" },
];

const CUENTA_TIPOS = ["Banco", "Efectivo", "Billetera digital", "Inversión"];

const DEUDA_TIPOS = [
  { id: "tarjeta_credito", label: "Tarjeta de crédito" },
  { id: "prestamo_personal", label: "Préstamo personal" },
  { id: "otro", label: "Otra deuda" },
];

const BOLSA_TIPOS = [
  { id: "libre", label: "Disponible" },
  { id: "normal", label: "Normal" },
  { id: "meta", label: "Meta" },
  { id: "inversion", label: "Inversión" },
  { id: "intocable", label: "Intocable" },
];

const BANCOS_SUGERIDOS = ["BBVA", "Banamex", "Santander", "HSBC", "Banorte", "Scotiabank", "Nu", "Mercado Pago", "Inbursa", "Banco Azteca", "Actinver", "STP", "Efectivo", "PayPal"];

const ORIGENES_INGRESO = ["Salario", "Negocio", "Inversión", "Freelance / Honorarios", "Renta", "Regalo", "Reembolso", "Rendimiento", "Otro ingreso"];

const CATEGORIAS_GASTO = ["Comida", "Transporte", "Entretenimiento", "Salud", "Hogar", "Servicios", "Ropa", "Educación", "Caprichos", "Otros"];

const FRECUENCIAS = ["Único", "Semanal", "Quincenal", "Mensual", "Bimestral", "Anual"];
const MESES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const BTN_SMALL = { background: GOLD, color: "#1A140C", border: "none", borderRadius: 8, padding: "7px 14px", fontWeight: 800, fontSize: 12.5, cursor: "pointer" };
const BTN_GHOST = { background: PANEL_2, color: "#F4EFE6", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 12px", fontWeight: 700, fontSize: 12.5, cursor: "pointer" };

function seedData() {
  return {
    cuentas: [
      { id: "c1", nombre: "BBVA", tipo: "Banco" },
      { id: "c2", nombre: "Efectivo", tipo: "Efectivo" },
    ],
    bolsas: [
      { id: "b1", nombre: "Disponible", color: BOLSA_COLORS[0], tipo: "libre", objetivo: 0, porcentaje: 30, orden: 0 },
      { id: "b2", nombre: "Emergencia", color: BOLSA_COLORS[1], tipo: "normal", objetivo: 0, porcentaje: 10, orden: 1 },
      { id: "b3", nombre: "Casa", color: BOLSA_COLORS[2], tipo: "normal", objetivo: 0, porcentaje: 20, orden: 2 },
      { id: "b4", nombre: "Retiro", color: BOLSA_COLORS[3], tipo: "intocable", objetivo: 0, porcentaje: 15, orden: 3 },
      { id: "b5", nombre: "Vacaciones", color: BOLSA_COLORS[4], tipo: "meta", objetivo: 25000, porcentaje: 10, orden: 4 },
      { id: "b6", nombre: "Impuestos", color: BOLSA_COLORS[5], tipo: "normal", objetivo: 0, porcentaje: 10, orden: 5 },
      { id: "b7", nombre: "Caprichos", color: BOLSA_COLORS[6], tipo: "normal", objetivo: 0, porcentaje: 5, orden: 6 },
    ],
    movimientos: [],
    deudas: [],
    prestamos: [],
    pagosProgramados: [],
    presupuestos: [
      { id: "p1", categoria: "Comida", limite: 4000 },
      { id: "p2", categoria: "Transporte", limite: 1500 },
      { id: "p3", categoria: "Entretenimiento", limite: 1000 },
    ],
    config: { discreto: false, moneda: "MXN", nombreUsuario: "", vistaCompacta: true },
  };
}

/* ============================== UTILIDADES ============================== */

function fmtMoney(n, discreto) {
  if (discreto) return "•••••";
  const v = Number(n) || 0;
  const sign = v < 0 ? "-" : "";
  return sign + "$" + Math.abs(v).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function mesActual() {
  return todayISO().slice(0, 7);
}
function daysUntil(dateStr) {
  const today = new Date(todayISO());
  const target = new Date(dateStr);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}
function genId(prefix) {
  return prefix + "_" + Math.random().toString(36).slice(2, 9);
}
function saludo() {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}
function uniq(arr) {
  return [...new Set(arr.filter(Boolean))];
}
function addMonthsUTC(f, n) {
  const dia = f.getUTCDate();
  f.setUTCDate(1);
  f.setUTCMonth(f.getUTCMonth() + n);
  const ult = new Date(Date.UTC(f.getUTCFullYear(), f.getUTCMonth() + 1, 0)).getUTCDate();
  f.setUTCDate(Math.min(dia, ult));
}
/** Suma `veces` periodos a una fecha ISO (YYYY-MM-DD) según la frecuencia. */
function addPeriodo(fechaISO, frecuencia, veces = 1) {
  const [y, m, d] = (fechaISO || todayISO()).split("-").map(Number);
  const f = new Date(Date.UTC(y, m - 1, d));
  for (let i = 0; i < veces; i++) {
    if (frecuencia === "Semanal") f.setUTCDate(f.getUTCDate() + 7);
    else if (frecuencia === "Quincenal") f.setUTCDate(f.getUTCDate() + 15);
    else if (frecuencia === "Bimestral") addMonthsUTC(f, 2);
    else if (frecuencia === "Anual") addMonthsUTC(f, 12);
    else addMonthsUTC(f, 1);
  }
  return f.toISOString().slice(0, 10);
}

/** Fuente única de verdad: los saldos se derivan reproduciendo los movimientos. */
function computeBalances(state) {
  const cuentaSaldos = {};
  state.cuentas.forEach((c) => (cuentaSaldos[c.id] = 0));
  const bolsaSaldos = {};
  state.bolsas.forEach((b) => (bolsaSaldos[b.id] = 0));
  const deudaPagos = {};
  const prestamoPagos = {};
  let totalRendimientos = 0;

  const ordenados = [...state.movimientos].sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0));

  ordenados.forEach((m) => {
    // Movimientos "históricos": ya ocurrieron antes de usar la app. Solo bajan el saldo
    // de la deuda o del préstamo; NO tocan cuentas ni bolsas de hoy.
    if (m.historico) {
      if (m.tipo === "pago_deuda") deudaPagos[m.deudaId] = (deudaPagos[m.deudaId] || 0) + m.importe;
      else if (m.tipo === "cobro_prestamo") prestamoPagos[m.prestamoId] = (prestamoPagos[m.prestamoId] || 0) + m.importe;
      return;
    }
    if (m.tipo === "ingreso") {
      cuentaSaldos[m.cuentaId] = (cuentaSaldos[m.cuentaId] || 0) + m.importe;
      (m.distribucion || []).forEach((d) => {
        bolsaSaldos[d.bolsaId] = (bolsaSaldos[d.bolsaId] || 0) + d.monto;
      });
    } else if (m.tipo === "gasto") {
      bolsaSaldos[m.bolsaId] = (bolsaSaldos[m.bolsaId] || 0) - m.importe;
      cuentaSaldos[m.cuentaId] = (cuentaSaldos[m.cuentaId] || 0) - m.importe;
    } else if (m.tipo === "transferencia_bolsas") {
      bolsaSaldos[m.bolsaId] = (bolsaSaldos[m.bolsaId] || 0) - m.importe;
      bolsaSaldos[m.bolsaDestinoId] = (bolsaSaldos[m.bolsaDestinoId] || 0) + m.importe;
    } else if (m.tipo === "pago_deuda") {
      bolsaSaldos[m.bolsaId] = (bolsaSaldos[m.bolsaId] || 0) - m.importe;
      cuentaSaldos[m.cuentaId] = (cuentaSaldos[m.cuentaId] || 0) - m.importe;
      deudaPagos[m.deudaId] = (deudaPagos[m.deudaId] || 0) + m.importe;
    } else if (m.tipo === "prestamo_otorgado") {
      bolsaSaldos[m.bolsaId] = (bolsaSaldos[m.bolsaId] || 0) - m.importe;
      cuentaSaldos[m.cuentaId] = (cuentaSaldos[m.cuentaId] || 0) - m.importe;
    } else if (m.tipo === "cobro_prestamo") {
      bolsaSaldos[m.bolsaId] = (bolsaSaldos[m.bolsaId] || 0) + m.importe;
      cuentaSaldos[m.cuentaId] = (cuentaSaldos[m.cuentaId] || 0) + m.importe;
      prestamoPagos[m.prestamoId] = (prestamoPagos[m.prestamoId] || 0) + m.importe;
    } else if (m.tipo === "rendimiento") {
      // Ganancia de una inversión: crece la bolsa, pero todavía no es efectivo en un banco.
      bolsaSaldos[m.bolsaId] = (bolsaSaldos[m.bolsaId] || 0) + m.importe;
      totalRendimientos += m.importe;
    } else if (m.tipo === "trabajo_ingreso") {
      // Cuenta de trabajo: solo informativa, no toca bolsas.
      cuentaSaldos[m.cuentaId] = (cuentaSaldos[m.cuentaId] || 0) + m.importe;
    } else if (m.tipo === "trabajo_gasto") {
      cuentaSaldos[m.cuentaId] = (cuentaSaldos[m.cuentaId] || 0) - m.importe;
    } else if (m.tipo === "ajuste") {
      // Conciliación: mueve cuenta y bolsa juntas, para nunca romper la igualdad entre ambas.
      bolsaSaldos[m.bolsaId] = (bolsaSaldos[m.bolsaId] || 0) + m.importe;
      cuentaSaldos[m.cuentaId] = (cuentaSaldos[m.cuentaId] || 0) + m.importe;
    }
  });

  return { cuentaSaldos, bolsaSaldos, deudaPagos, prestamoPagos, totalRendimientos, ordenados };
}

function computePatrimonioHistoria(state, balancesInfo) {
  const bolsaSaldosRunning = {};
  state.bolsas.forEach((b) => (bolsaSaldosRunning[b.id] = 0));
  const deudaPagosRunning = {};

  const porDia = {};
  balancesInfo.ordenados.forEach((m) => {
    if (m.historico && m.tipo !== "pago_deuda") return;
    if (m.tipo === "ingreso") {
      (m.distribucion || []).forEach((d) => {
        bolsaSaldosRunning[d.bolsaId] = (bolsaSaldosRunning[d.bolsaId] || 0) + d.monto;
      });
    } else if (m.tipo === "gasto") {
      bolsaSaldosRunning[m.bolsaId] = (bolsaSaldosRunning[m.bolsaId] || 0) - m.importe;
    } else if (m.tipo === "transferencia_bolsas") {
      bolsaSaldosRunning[m.bolsaId] = (bolsaSaldosRunning[m.bolsaId] || 0) - m.importe;
      bolsaSaldosRunning[m.bolsaDestinoId] = (bolsaSaldosRunning[m.bolsaDestinoId] || 0) + m.importe;
    } else if (m.tipo === "pago_deuda") {
      if (!m.historico) bolsaSaldosRunning[m.bolsaId] = (bolsaSaldosRunning[m.bolsaId] || 0) - m.importe;
      deudaPagosRunning[m.deudaId] = (deudaPagosRunning[m.deudaId] || 0) + m.importe;
    }
    const sumaBolsas = Object.values(bolsaSaldosRunning).reduce((a, b) => a + b, 0);
    const sumaDeudas = state.deudas.reduce((acc, d) => {
      const pagado = deudaPagosRunning[d.id] || 0;
      return acc + Math.max(d.importeOriginal - pagado, 0);
    }, 0);
    porDia[m.fecha] = sumaBolsas - sumaDeudas;
  });

  const puntos = Object.entries(porDia).map(([fecha, valor]) => ({ fecha, valor }));
  puntos.sort((a, b) => (a.fecha < b.fecha ? -1 : 1));
  return puntos.slice(-30);
}

function computeResumenMensual(state, disponibleBolsaId) {
  const mes = mesActual();
  const delMes = state.movimientos.filter((m) => m.fecha.startsWith(mes));
  const ingresos = delMes.filter((m) => m.tipo === "ingreso").reduce((a, m) => a + m.importe, 0);
  const gastos = delMes.filter((m) => m.tipo === "gasto").reduce((a, m) => a + m.importe, 0);
  let ahorro = 0;
  delMes.forEach((m) => {
    if (m.tipo === "ingreso") {
      (m.distribucion || []).forEach((d) => {
        if (d.bolsaId !== disponibleBolsaId) ahorro += d.monto;
      });
    }
    if (m.tipo === "transferencia_bolsas" && m.bolsaDestinoId !== disponibleBolsaId) ahorro += m.importe;
  });
  return { ingresos, gastos, ahorro };
}

function computeGastoPorCategoria(state, soloMes) {
  const map = {};
  const mes = mesActual();
  state.movimientos.filter((m) => m.tipo === "gasto" && (!soloMes || (m.fecha || "").startsWith(mes))).forEach((m) => {
    const cat = m.categoria || "Sin categoría";
    map[cat] = (map[cat] || 0) + m.importe;
  });
  return Object.entries(map).map(([categoria, total]) => ({ categoria, total })).sort((a, b) => b.total - a.total);
}

/* ============================== COMPONENTES BASE ============================== */

function Money({ value, discreto, size = "base", tone }) {
  const sizes = { sm: "14px", base: "20px", lg: "28px", xl: "36px" };
  return (
    <span style={{ fontSize: sizes[size], fontWeight: 600, color: tone || "#F4EFE6", fontVariantNumeric: "tabular-nums" }}>
      {fmtMoney(value, discreto)}
    </span>
  );
}

function Card({ children, style, onClick }) {
  return (
    <div onClick={onClick} style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 18, ...style }}>
      {children}
    </div>
  );
}

function IconCircle({ color, children, size = 38 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: `${color}22`, border: `1px solid ${color}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 50 }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 20, width: "100%", maxWidth: wide ? 560 : 440, maxHeight: "88vh", overflowY: "auto", padding: 20, marginBottom: 24 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ color: "#F4EFE6", fontSize: 17, fontWeight: 700, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer", padding: 6 }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, color: TEXT_MUTED, marginBottom: 6, fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = { width: "100%", background: PANEL_2, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px", color: "#F4EFE6", fontSize: 15, outline: "none", boxSizing: "border-box" };

function TextInput(props) {
  return <input {...props} style={{ ...inputStyle, ...(props.style || {}) }} />;
}
function Select(props) {
  return <select {...props} style={{ ...inputStyle, ...(props.style || {}) }} />;
}
function DataListInput({ options, ...props }) {
  const listId = props.id || "dl_" + Math.random().toString(36).slice(2, 8);
  return (
    <>
      <input {...props} list={listId} style={{ ...inputStyle, ...(props.style || {}) }} />
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

function PrimaryButton({ children, onClick, disabled, style }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ background: disabled ? "#4a3f2c" : GOLD, color: "#1A140C", border: "none", borderRadius: 10, padding: "12px 16px", fontWeight: 700, fontSize: 15, cursor: disabled ? "not-allowed" : "pointer", width: "100%", opacity: disabled ? 0.6 : 1, ...style }}>
      {children}
    </button>
  );
}

function EmptyHint({ text }) {
  return <div style={{ fontSize: 13, color: TEXT_MUTED, padding: "18px 0", textAlign: "center" }}>{text}</div>;
}

function ViewHeader({ title, subtitle, onNuevo, nuevoLabel }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{title}</h2>
        <div style={{ fontSize: 12, color: TEXT_MUTED }}>{subtitle}</div>
      </div>
      {onNuevo && (
        <button onClick={onNuevo} style={{ background: GOLD, color: "#1A140C", border: "none", borderRadius: 10, padding: "10px 16px", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <Plus size={16} /> {nuevoLabel}
        </button>
      )}
    </div>
  );
}

/* ============================== APP ============================== */

const NAV_ITEMS = [
  { id: "dashboard", label: "Inicio", icon: LayoutGrid },
  { id: "movimientos", label: "Movimientos", icon: ListTree },
  { id: "cuentas", label: "Cuentas", icon: Building2 },
  { id: "bolsas", label: "Bolsas", icon: PiggyBank },
  { id: "deudas", label: "Deudas", icon: CreditCard },
  { id: "prestamos", label: "Préstamos", icon: Users },
  { id: "pagos", label: "Pagos Programados", icon: CalendarClock },
  { id: "presupuestos", label: "Presupuestos", icon: SlidersHorizontal },
  { id: "metas", label: "Metas", icon: Target },
  { id: "patrimonio", label: "Patrimonio", icon: TrendingUp },
  { id: "reportes", label: "Reportes", icon: BarChart3 },
  { id: "config", label: "Configuración", icon: Settings },
];

function PPCacaoApp({ uid, userEmail }) {
  const [data, setData] = useState(seedData());
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState("dashboard");
  const [modal, setModal] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const showSidebar = width >= 760;
  const cols2 = width >= 560 ? 2 : 1;
  const cols4 = width >= 900 ? 4 : width >= 560 ? 2 : 1;
  const wideRow = width >= 900;

  const docRef = doc(db, "users", uid, "data", "main");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const parsed = snap.data();
          setData({ ...seedData(), ...parsed });
        }
      } catch (e) { /* sin datos previos */ }
      finally { setLoaded(true); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    if (!loaded) return;
    setSaveState("saving");
    const t = setTimeout(async () => {
      try {
        // JSON.parse(JSON.stringify()) limpia cualquier valor "undefined" que
        // Firestore no acepta y que puede tumbar el guardado silenciosamente.
        const limpio = JSON.parse(JSON.stringify(data));
        await setDoc(docRef, limpio);
        setSaveState("saved");
      } catch (e) {
        console.error("Error guardando en Firestore:", e);
        setSaveState("idle");
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loaded]);

  const discreto = data.config.discreto;
  const balancesInfo = useMemo(() => computeBalances(data), [data]);
  const { cuentaSaldos, bolsaSaldos, deudaPagos, prestamoPagos, totalRendimientos } = balancesInfo;

  const disponibleBolsa = data.bolsas.find((b) => b.tipo === "libre");
  const disponible = disponibleBolsa ? bolsaSaldos[disponibleBolsa.id] || 0 : 0;
  const totalBolsas = data.bolsas.reduce((acc, b) => acc + (bolsaSaldos[b.id] || 0), 0);
  const totalApartado = totalBolsas - disponible;
  // Cuentas de "Trabajo" son solo de control: no entran a totales, auditoría ni patrimonio.
  const cuentasPat = data.cuentas.filter((c) => c.clase !== "trabajo");
  const cuentasTrabajo = data.cuentas.filter((c) => c.clase === "trabajo");
  const totalCuentas = cuentasPat.reduce((acc, c) => acc + (cuentaSaldos[c.id] || 0), 0);
  const deudasActivas = data.deudas.filter((d) => (d.importeOriginal - (deudaPagos[d.id] || 0)) > 0);
  const totalDeuda = deudasActivas.reduce((acc, d) => acc + Math.max(d.importeOriginal - (deudaPagos[d.id] || 0), 0), 0);
  const prestamosActivos = data.prestamos.filter((p) => (p.importeOriginal - (prestamoPagos[p.id] || 0)) > 0);
  const totalPrestado = prestamosActivos.reduce((acc, p) => acc + Math.max(p.importeOriginal - (prestamoPagos[p.id] || 0), 0), 0);
  const patrimonio = totalBolsas - totalDeuda + totalPrestado;
  // Diferencia de auditoría: cuentas vs. bolsas, quitando rendimientos (que no son efectivo real todavía).
  const diferenciaAuditoria = totalCuentas - (totalBolsas - totalRendimientos);

  const historia = useMemo(() => computePatrimonioHistoria(data, balancesInfo), [data, balancesInfo]);
  const resumenMensual = useMemo(() => computeResumenMensual(data, disponibleBolsa?.id), [data, disponibleBolsa]);
  const gastoPorCategoria = useMemo(() => computeGastoPorCategoria(data), [data]);
  const gastoMesPorCategoria = useMemo(() => computeGastoPorCategoria(data, true), [data]);

  const proximoPago = useMemo(() => {
    return deudasActivas.map((d) => ({ ...d, dias: daysUntil(d.fechaPago) })).sort((a, b) => a.dias - b.dias)[0];
  }, [deudasActivas]);

  const pagosProximos = useMemo(() => {
    return [...data.pagosProgramados].map((p) => ({ ...p, dias: daysUntil(p.fechaProxima) })).sort((a, b) => a.dias - b.dias);
  }, [data.pagosProgramados]);

  const alertas = useMemo(() => {
    const list = [];
    deudasActivas.forEach((d) => {
      const dias = daysUntil(d.fechaPago);
      if (dias <= 5) list.push({ tipo: "deuda", texto: `${d.acreedor}: ${dias < 0 ? "pago vencido" : dias === 0 ? "vence hoy" : `vence en ${dias}d`}`, urgente: dias < 0 || dias <= 3 });
    });
    pagosProximos.forEach((p) => {
      if (p.dias <= 5) list.push({ tipo: "pago", texto: `${p.nombre}: ${p.dias < 0 ? "pasó la fecha" : p.dias === 0 ? "es hoy" : `en ${p.dias}d`}`, urgente: p.dias <= 2 });
    });
    return list;
  }, [deudasActivas, pagosProximos]);

  const semaforo = useMemo(() => {
    if (proximoPago && proximoPago.dias < 0) return { color: RED, label: "Hay un pago vencido" };
    if (proximoPago && proximoPago.dias <= 3 && disponible < proximoPago.importeOriginal - (deudaPagos[proximoPago.id] || 0)) return { color: RED, label: "No te alcanza para el próximo pago" };
    if (proximoPago && proximoPago.dias <= 7) return { color: YELLOW, label: "Tienes un pago acercándose" };
    if (disponible < 0) return { color: RED, label: "Tu disponible está en negativo" };
    if (totalDeuda > totalBolsas * 0.6) return { color: YELLOW, label: "Tus deudas pesan mucho" };
    return { color: GREEN, label: "Todo bajo control. Vas bien." };
  }, [proximoPago, disponible, totalDeuda, totalBolsas, deudaPagos]);

  const ultimosMovimientos = [...data.movimientos].sort((a, b) => (a.fecha < b.fecha ? 1 : -1)).slice(0, 5);

  const nombresBancosUsados = uniq(data.cuentas.map((c) => c.nombre));
  const origenesUsados = uniq(data.movimientos.filter((m) => m.tipo === "ingreso").map((m) => m.origen));
  const categoriasUsadas = uniq(data.movimientos.filter((m) => m.tipo === "gasto").map((m) => m.categoria));

  /* ------------ acciones ------------ */
  const toggleDiscreto = () => setData((d) => ({ ...d, config: { ...d.config, discreto: !d.config.discreto } }));
  const addMovimiento = (m) => {
    const { crearPrestamo, ...limpio } = m;
    setData((d) => ({
      ...d,
      prestamos: crearPrestamo ? [...d.prestamos, crearPrestamo] : d.prestamos,
      movimientos: [...d.movimientos, { ...limpio, id: genId("m"), fecha: limpio.fecha || todayISO() }],
    }));
  };
  const deleteMovimiento = (id) => setData((d) => ({ ...d, movimientos: d.movimientos.filter((m) => m.id !== id) }));

  const addBolsa = (b) => setData((d) => ({ ...d, bolsas: [...d.bolsas, { ...b, id: genId("b"), orden: d.bolsas.length }] }));
  const updateBolsa = (id, patch) => setData((d) => ({ ...d, bolsas: d.bolsas.map((b) => (b.id === id ? { ...b, ...patch } : b)) }));
  const deleteBolsa = (id) => setData((d) => ({ ...d, bolsas: d.bolsas.filter((b) => b.id !== id) }));

  const addCuenta = (c) => setData((d) => ({ ...d, cuentas: [...d.cuentas, { ...c, id: genId("c") }] }));
  const deleteCuenta = (id) => setData((d) => ({ ...d, cuentas: d.cuentas.filter((c) => c.id !== id) }));

  const addDeuda = (deu, historicos = []) => setData((d) => {
    const id = genId("d");
    const movs = historicos.map((h) => ({ ...h, id: genId("m"), tipo: "pago_deuda", deudaId: id, historico: true }));
    return { ...d, deudas: [...d.deudas, { ...deu, id }], movimientos: [...d.movimientos, ...movs] };
  });
  const deleteDeuda = (id) => setData((d) => ({ ...d, deudas: d.deudas.filter((x) => x.id !== id) }));
  const abonarDeuda = (deudaId, mov, proximaFecha) => setData((d) => ({
    ...d,
    movimientos: [...d.movimientos, { ...mov, id: genId("m"), tipo: "pago_deuda", deudaId }],
    deudas: d.deudas.map((x) => (x.id === deudaId && proximaFecha ? { ...x, fechaPago: proximaFecha } : x)),
  }));
  const cobrarPrestamo = (prestamoId, mov, proximaFecha) => setData((d) => ({
    ...d,
    movimientos: [...d.movimientos, { ...mov, id: genId("m"), tipo: "cobro_prestamo", prestamoId }],
    prestamos: d.prestamos.map((x) => (x.id === prestamoId ? { ...x, fechaCobroEsperada: proximaFecha || "" } : x)),
  }));
  const pagarProgramado = (pagoId, mov, nuevaFecha, eliminar) => setData((d) => ({
    ...d,
    movimientos: [...d.movimientos, { ...mov, id: genId("m"), tipo: "gasto", pagoProgramadoId: pagoId }],
    pagosProgramados: eliminar
      ? d.pagosProgramados.filter((p) => p.id !== pagoId)
      : d.pagosProgramados.map((p) => (p.id === pagoId ? { ...p, ...(nuevaFecha ? { fechaProxima: nuevaFecha } : {}), ultimoMonto: mov.importe } : p)),
  }));
  const asignarFaltanteADisponible = () => setData((d) => {
    const falta = 100 - d.bolsas.reduce((a, b) => a + (b.porcentaje || 0), 0);
    const libre = d.bolsas.find((b) => b.tipo === "libre");
    if (falta <= 0 || !libre) return d;
    return { ...d, bolsas: d.bolsas.map((b) => (b.id === libre.id ? { ...b, porcentaje: (b.porcentaje || 0) + falta } : b)) };
  });

  const addPrestamo = (p) => setData((d) => ({ ...d, prestamos: [...d.prestamos, { ...p, id: genId("prs") }] }));
  const deletePrestamo = (id) => setData((d) => ({ ...d, prestamos: d.prestamos.filter((x) => x.id !== id) }));

  const addPago = (p) => setData((d) => ({ ...d, pagosProgramados: [...d.pagosProgramados, { ...p, id: genId("pp") }] }));
  const deletePago = (id) => setData((d) => ({ ...d, pagosProgramados: d.pagosProgramados.filter((p) => p.id !== id) }));

  const addPresupuesto = (p) => setData((d) => ({ ...d, presupuestos: [...d.presupuestos, { ...p, id: genId("pr") }] }));
  const deletePresupuesto = (id) => setData((d) => ({ ...d, presupuestos: d.presupuestos.filter((p) => p.id !== id) }));

  const updateConfig = (patch) => setData((d) => ({ ...d, config: { ...d.config, ...patch } }));
  const updateBolsaPct = (id, pct) => setData((d) => ({ ...d, bolsas: d.bolsas.map((b) => (b.id === id ? { ...b, porcentaje: pct } : b)) }));

  if (!loaded) {
    return <div style={{ background: BG, minHeight: 600, display: "flex", alignItems: "center", justifyContent: "center", color: TEXT_MUTED }}>Cargando tu información…</div>;
  }

  return (
    <div style={{ background: BG, minHeight: 640, color: "#F4EFE6", fontFamily: "'Inter', 'Segoe UI', sans-serif", display: "flex", flexDirection: "column" }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-thumb { background: #2C2419; border-radius: 8px; }
        .navbtn { display:flex; flex-direction:column; align-items:center; gap:4px; padding:8px 4px; border-radius:12px; cursor:pointer; color:${TEXT_MUTED}; }
        .navbtn.active { color:${GOLD_LIGHT}; }
        .sidebtn { display:flex; align-items:center; gap:10px; padding:9px 14px; border-radius:10px; cursor:pointer; color:${TEXT_MUTED}; font-size:13.5px; font-weight:600; }
        .sidebtn.active { background: rgba(201,161,90,0.12); color:${GOLD_LIGHT}; }
        .sidebtn:hover { color:${GOLD_LIGHT}; }
        input[type=range] { accent-color: ${GOLD}; }
        table.rep { width:100%; border-collapse: collapse; font-size:13px; }
        table.rep th { text-align:left; color:${TEXT_MUTED}; font-weight:600; font-size:11px; text-transform:uppercase; letter-spacing:0.3px; padding:8px 6px; border-bottom:1px solid ${BORDER}; }
        table.rep td { padding:9px 6px; border-bottom:1px solid ${BORDER}; }
      `}</style>

      <Header discreto={discreto} toggleDiscreto={toggleDiscreto} saveState={saveState} alertas={alertas} onOpenAlertas={() => setModal({ type: "alertas" })} onOpenMenu={() => setModal({ type: "menu" })} showSidebar={showSidebar} />

      <div style={{ display: "flex", flex: 1, maxWidth: 1180, width: "100%", margin: "0 auto" }}>
        {showSidebar && <Sidebar view={view} setView={setView} userEmail={userEmail} />}

        <main style={{ flex: 1, padding: "18px 16px calc(90px + env(safe-area-inset-bottom))", minWidth: 0 }}>
          {view === "dashboard" && (
            <Dashboard discreto={discreto} disponible={disponible} totalApartado={totalApartado} totalDeuda={totalDeuda} patrimonio={patrimonio} semaforo={semaforo} bolsas={data.bolsas} bolsaSaldos={bolsaSaldos} proximoPago={proximoPago} deudaPagos={deudaPagos} ultimosMovimientos={ultimosMovimientos} cuentas={data.cuentas} historia={historia} resumenMensual={resumenMensual} onNuevoMovimiento={() => setModal({ type: "movimiento" })} deudasActivas={deudasActivas} cols4={cols4} wideRow={wideRow} nombreUsuario={data.config.nombreUsuario} vistaCompacta={data.config.vistaCompacta} onToggleVista={() => updateConfig({ vistaCompacta: !data.config.vistaCompacta })} />
          )}
          {view === "movimientos" && (
            <MovimientosView discreto={discreto} movimientos={data.movimientos} bolsas={data.bolsas} cuentas={data.cuentas} onNuevo={() => setModal({ type: "movimiento" })} onDelete={deleteMovimiento} />
          )}
          {view === "cuentas" && (
            <CuentasView discreto={discreto} cuentas={data.cuentas} cuentaSaldos={cuentaSaldos} movimientos={data.movimientos} bolsas={data.bolsas} onNueva={() => setModal({ type: "cuenta" })} onDelete={deleteCuenta} cols2={cols2} />
          )}
          {view === "bolsas" && (
            <BolsasView discreto={discreto} bolsas={data.bolsas} bolsaSaldos={bolsaSaldos} onNueva={() => setModal({ type: "bolsa" })} onDelete={deleteBolsa} onEdit={(b) => setModal({ type: "bolsa", payload: b })} cols2={cols2} />
          )}
          {view === "deudas" && (
            <DeudasView discreto={discreto} deudas={data.deudas} deudaPagos={deudaPagos} movimientos={data.movimientos} cuentas={data.cuentas} bolsas={data.bolsas} onNueva={() => setModal({ type: "deuda" })} onDelete={deleteDeuda} onAbonar={(d) => setModal({ type: "abono", payload: d })} />
          )}
          {view === "prestamos" && (
            <PrestamosView discreto={discreto} prestamos={data.prestamos} prestamoPagos={prestamoPagos} movimientos={data.movimientos} cuentas={data.cuentas} bolsas={data.bolsas} onNuevo={() => setModal({ type: "prestamo" })} onDelete={deletePrestamo} onCobrar={(p) => setModal({ type: "cobro", payload: p })} />
          )}
          {view === "pagos" && (
            <PagosProgramadosView discreto={discreto} pagos={pagosProximos} movimientos={data.movimientos} onNuevo={() => setModal({ type: "pago" })} onDelete={deletePago} onPagar={(p) => setModal({ type: "pagarProg", payload: p })} />
          )}
          {view === "presupuestos" && (
            <PresupuestosView discreto={discreto} presupuestos={data.presupuestos} gastoPorCategoria={gastoMesPorCategoria} onNuevo={() => setModal({ type: "presupuesto" })} onDelete={deletePresupuesto} cols2={cols2} />
          )}
          {view === "metas" && (
            <MetasView discreto={discreto} bolsas={data.bolsas} bolsaSaldos={bolsaSaldos} onNueva={() => setModal({ type: "meta" })} cols2={cols2} />
          )}
          {view === "patrimonio" && (
            <PatrimonioView discreto={discreto} patrimonio={patrimonio} totalBolsas={totalBolsas} totalCuentas={totalCuentas} totalDeuda={totalDeuda} totalPrestado={totalPrestado} totalRendimientos={totalRendimientos} diferenciaAuditoria={diferenciaAuditoria} bolsas={data.bolsas} bolsaSaldos={bolsaSaldos} cuentas={cuentasPat} cuentaSaldos={cuentaSaldos} deudas={deudasActivas} deudaPagos={deudaPagos} prestamos={prestamosActivos} prestamoPagos={prestamoPagos} historia={historia} cols2={cols2} />
          )}
          {view === "reportes" && (
            <ReportesView discreto={discreto} disponible={disponible} totalApartado={totalApartado} totalDeuda={totalDeuda} resumenMensual={resumenMensual} gastoPorCategoria={gastoPorCategoria} historia={historia} cols2={cols2} />
          )}
          {view === "config" && (
            <ConfigView bolsas={data.bolsas} onUpdatePct={updateBolsaPct} config={data.config} onUpdateConfig={updateConfig} onAsignarADisponible={asignarFaltanteADisponible} onCrearBolsaFaltante={(pct) => setModal({ type: "bolsa", payload: { porcentaje: pct } })} />
          )}
        </main>
      </div>

      {!showSidebar && <MobileNav view={view} setView={setView} onMore={() => setModal({ type: "menu" })} />}

      {modal?.type === "movimiento" && (
        <MovimientoModal bolsas={data.bolsas} cuentas={cuentasPat} cuentasTrabajo={cuentasTrabajo} deudas={data.deudas} deudaPagos={deudaPagos} prestamos={data.prestamos} prestamoPagos={prestamoPagos} bolsaSaldos={bolsaSaldos} origenesUsados={origenesUsados} categoriasUsadas={categoriasUsadas} onClose={() => setModal(null)} onSave={(m) => { addMovimiento(m); setModal(null); }} />
      )}
      {modal?.type === "bolsa" && (
        <BolsaModal bolsa={modal.payload} otrosPct={data.bolsas.filter((x) => x.id !== modal.payload?.id).reduce((a, x) => a + (x.porcentaje || 0), 0)} onClose={() => setModal(null)} onSave={(b) => { modal.payload?.id ? updateBolsa(modal.payload.id, b) : addBolsa(b); setModal(null); }} />
      )}
      {modal?.type === "meta" && (
        <BolsaModal bolsa={{ tipo: "meta" }} forceMeta onClose={() => setModal(null)} onSave={(b) => { addBolsa(b); setModal(null); }} />
      )}
      {modal?.type === "cuenta" && (
        <CuentaModal bancosUsados={nombresBancosUsados} onClose={() => setModal(null)} onSave={(c) => { addCuenta(c); setModal(null); }} />
      )}
      {modal?.type === "deuda" && (
        <DeudaModal onClose={() => setModal(null)} onSave={(deu) => { addDeuda(deu); setModal(null); }} />
      )}
      {modal?.type === "prestamo" && (
        <PrestamoModal onClose={() => setModal(null)} onSave={(p) => { addPrestamo(p); setModal(null); }} />
      )}
      {modal?.type === "pago" && (
        <PagoProgramadoModal cuentas={cuentasPat} bolsas={data.bolsas} onClose={() => setModal(null)} onSave={(p) => { addPago(p); setModal(null); }} />
      )}
      {modal?.type === "abono" && (
        <AbonoDeudaModal deuda={modal.payload} saldo={Math.max(modal.payload.importeOriginal - (deudaPagos[modal.payload.id] || 0), 0)} cuentas={cuentasPat} bolsas={data.bolsas} bolsaSaldos={bolsaSaldos} onClose={() => setModal(null)} onSave={(mov, prox) => { abonarDeuda(modal.payload.id, mov, prox); setModal(null); }} />
      )}
      {modal?.type === "cobro" && (
        <CobroPrestamoModal prestamo={modal.payload} saldo={Math.max(modal.payload.importeOriginal - (prestamoPagos[modal.payload.id] || 0), 0)} cuentas={cuentasPat} bolsas={data.bolsas} onClose={() => setModal(null)} onSave={(mov, prox) => { cobrarPrestamo(modal.payload.id, mov, prox); setModal(null); }} />
      )}
      {modal?.type === "pagarProg" && (
        <PagarProgramadoModal pago={modal.payload} cuentas={cuentasPat} bolsas={data.bolsas} bolsaSaldos={bolsaSaldos} categoriasUsadas={categoriasUsadas} onClose={() => setModal(null)} onSave={(mov, fecha, eliminar) => { pagarProgramado(modal.payload.id, mov, fecha, eliminar); setModal(null); }} />
      )}
      {modal?.type === "presupuesto" && (
        <PresupuestoModal categoriasUsadas={categoriasUsadas} onClose={() => setModal(null)} onSave={(p) => { addPresupuesto(p); setModal(null); }} />
      )}
      {modal?.type === "alertas" && (
        <Modal title="Alertas" onClose={() => setModal(null)}>
          {alertas.length === 0 ? <EmptyHint text="No tienes alertas por ahora." /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {alertas.map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "8px 10px", borderRadius: 8, background: PANEL_2, border: `1px solid ${a.urgente ? RED : YELLOW}55` }}>
                  <AlertTriangle size={14} color={a.urgente ? RED : YELLOW} />
                  {a.texto}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
      {modal?.type === "menu" && (
        <Modal title="Menú" onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 14 }}>
            <img src={LOGO_LIGHT_DATA_URI} alt="PP Cacao" style={{ width: 84, height: 84, objectFit: "contain" }} />
            {userEmail && <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 6 }}>{userEmail}</div>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {NAV_ITEMS.map((item) => (
              <div key={item.id} className={`sidebtn ${view === item.id ? "active" : ""}`} onClick={() => { setView(item.id); setModal(null); }}>
                <item.icon size={17} />
                {item.label}
              </div>
            ))}
            <div className="sidebtn" onClick={() => signOut(auth)} style={{ color: RED }}>
              <LogOut size={17} />
              Cerrar sesión
            </div>
          </div>
          <div style={{ textAlign: "center", fontSize: 10, color: TEXT_MUTED, opacity: 0.7, marginTop: 16 }}>
            {APP_VERSION} · {APP_AUTHOR}
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============================== HEADER / NAV ============================== */

function Header({ discreto, toggleDiscreto, saveState, alertas, onOpenAlertas, onOpenMenu, showSidebar }) {
  return (
    <div style={{ borderBottom: `1px solid ${BORDER}`, background: BG }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!showSidebar && (
            <button onClick={onOpenMenu} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer", padding: 4 }}>
              <Menu size={20} />
            </button>
          )}
          <img src={LOGO_LIGHT_DATA_URI} alt="PP Cacao" style={{ width: 54, height: 54, objectFit: "contain", flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 19, letterSpacing: 0.4, color: "#F4EFE6" }}>PP Cacao</div>
            <div style={{ fontSize: 11, color: TEXT_MUTED, letterSpacing: 0.5 }}>QUE NUNCA TE FALTE TU CACAITO</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {showSidebar && <span style={{ fontSize: 11, color: TEXT_MUTED }}>{saveState === "saving" ? "Guardando…" : "Guardado"}</span>}
          <button onClick={onOpenAlertas} style={{ position: "relative", background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 10, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: GOLD_LIGHT }}>
            <Bell size={16} />
            {alertas.length > 0 && (
              <span style={{ position: "absolute", top: -4, right: -4, background: RED, color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 999, minWidth: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>
                {alertas.length}
              </span>
            )}
          </button>
          <button onClick={toggleDiscreto} title={discreto ? "Mostrar montos" : "Ocultar montos"} style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 10, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: GOLD_LIGHT }}>
            {discreto ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ view, setView, userEmail }) {
  return (
    <aside style={{ width: 220, flexShrink: 0, padding: "20px 12px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 22, padding: "0 8px" }}>
          <img src={LOGO_LIGHT_DATA_URI} alt="PP Cacao" style={{ width: 108, height: 108, objectFit: "contain" }} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: 0.4, color: "#F4EFE6" }}>PP Cacao</div>
            <div style={{ fontSize: 9.5, color: TEXT_MUTED, letterSpacing: 0.5 }}>QUE NUNCA TE FALTE TU CACAITO</div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {NAV_ITEMS.map((item) => (
            <div key={item.id} className={`sidebtn ${view === item.id ? "active" : ""}`} onClick={() => setView(item.id)}>
              <item.icon size={16} />
              {item.label}
            </div>
          ))}
          <div className="sidebtn" onClick={() => signOut(auth)} style={{ color: RED, marginTop: 8 }}>
            <LogOut size={16} />
            Cerrar sesión
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", paddingTop: 16 }}>
        {userEmail && <div style={{ fontSize: 10, color: TEXT_MUTED, marginBottom: 4, wordBreak: "break-all" }}>{userEmail}</div>}
        <div style={{ fontSize: 10, color: TEXT_MUTED, opacity: 0.7 }}>{APP_VERSION} · {APP_AUTHOR}</div>
      </div>
    </aside>
  );
}

function MobileNav({ view, setView, onMore }) {
  const items = NAV_ITEMS.filter((i) => ["dashboard", "movimientos", "bolsas", "deudas"].includes(i.id));
  return (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: PANEL, borderTop: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-around", padding: "8px 6px", paddingBottom: "calc(8px + env(safe-area-inset-bottom))", zIndex: 30 }}>
      {items.map((item) => (
        <div key={item.id} className={`navbtn ${view === item.id ? "active" : ""}`} onClick={() => setView(item.id)} style={{ fontSize: 10 }}>
          <item.icon size={20} />
          {item.label}
        </div>
      ))}
      <div className="navbtn" onClick={onMore} style={{ fontSize: 10 }}>
        <Menu size={20} />
        Más
      </div>
    </div>
  );
}

/* ============================== DASHBOARD ============================== */

function Dashboard({ discreto, disponible, totalApartado, totalDeuda, patrimonio, semaforo, bolsas, bolsaSaldos, proximoPago, deudaPagos, ultimosMovimientos, cuentas, historia, resumenMensual, onNuevoMovimiento, deudasActivas, cols4, wideRow, nombreUsuario, vistaCompacta, onToggleVista }) {
  const pieData = bolsas.map((b) => ({ name: b.nombre, value: Math.max(bolsaSaldos[b.id] || 0, 0), color: b.color })).filter((d) => d.value > 0);
  const cuentaMap = Object.fromEntries(cuentas.map((c) => [c.id, c.nombre]));
  const bolsaMap = Object.fromEntries(bolsas.map((b) => [b.id, b]));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 21, fontWeight: 800, margin: 0, lineHeight: 1.3 }}>
          {saludo()}
          {nombreUsuario && <>, <span style={{ fontSize: 27, fontWeight: 900, color: GOLD_LIGHT }}>{nombreUsuario.toUpperCase()}</span></>} 👋
        </h2>
        <div style={{ fontSize: 12.5, color: TEXT_MUTED, marginTop: 4 }}>
          Tu resumen financiero de hoy · {new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}
        </div>
      </div>

      <Card style={{ marginBottom: 14, background: `linear-gradient(135deg, ${PANEL_2}, ${PANEL})`, border: `1px solid ${GOLD}44` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <IconCircle color={GREEN} size={32}><Wallet size={16} color={GREEN} /></IconCircle>
              <span style={{ fontSize: 12, color: TEXT_MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Disponible hoy</span>
            </div>
            <Money value={disponible} discreto={discreto} size="xl" />
            <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 4 }}>Esto es lo único que puedes gastar libremente</div>
          </div>
          <button onClick={onNuevoMovimiento} style={{ background: GOLD, color: "#1A140C", border: "none", borderRadius: 12, padding: "14px 20px", fontWeight: 800, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, boxShadow: "0 4px 14px rgba(0,0,0,0.35)" }}>
            <Plus size={20} /> Nuevo movimiento
          </button>
        </div>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Semáforo financiero</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: semaforo.color, flexShrink: 0 }} />
              <div style={{ fontWeight: 700, fontSize: 14 }}>{semaforo.label}</div>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Próximo pago</div>
            {proximoPago ? (
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{proximoPago.acreedor}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 4 }}>
                  <Money value={Math.max(proximoPago.importeOriginal - (deudaPagos[proximoPago.id] || 0), 0)} discreto={discreto} size="base" />
                  <span style={{ fontSize: 12, color: proximoPago.dias < 0 ? RED : proximoPago.dias <= 3 ? YELLOW : TEXT_MUTED }}>
                    {proximoPago.dias < 0 ? `Vencido hace ${Math.abs(proximoPago.dias)}d` : proximoPago.dias === 0 ? "Vence hoy" : `Vence en ${proximoPago.dias}d`}
                  </span>
                </div>
              </div>
            ) : <div style={{ fontSize: 13, color: TEXT_MUTED }}>No tienes pagos pendientes.</div>}
          </div>
        </div>
      </Card>

      <button
        onClick={onToggleVista}
        style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: TEXT_MUTED, fontSize: 12.5, fontWeight: 700, cursor: "pointer", padding: "6px 2px", marginBottom: 12 }}
      >
        {vistaCompacta ? <Eye size={14} /> : <EyeOff size={14} />}
        {vistaCompacta ? "Ver mi panorama financiero completo" : "Ocultar panorama completo (modo privado)"}
      </button>

      {!vistaCompacta && (
      <>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols4 === 4 ? 3 : cols4},1fr)`, gap: 12, marginBottom: 14 }}>
        <StatCard icon={PiggyBank} color={GOLD} label="Dinero apartado" value={totalApartado} discreto={discreto} sub="Ya tiene destino" />
        <StatCard icon={CreditCard} color={RED} label="Deudas" value={totalDeuda} discreto={discreto} sub={`${deudasActivas.length} activa(s)`} />
        <StatCard icon={TrendingUp} color={GOLD_LIGHT} label="Patrimonio neto" value={patrimonio} discreto={discreto} sub="Bolsas menos deudas" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Distribución en bolsas</div>
          {pieData.length === 0 ? <EmptyHint text="Registra un ingreso y verás aquí cómo se reparte tu dinero." /> : (
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ width: 160, height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2} stroke="none">
                      {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: 1, minWidth: 160, display: "flex", flexDirection: "column", gap: 7 }}>
                {bolsas.map((b) => {
                  const val = bolsaSaldos[b.id] || 0;
                  if (val <= 0) return null;
                  return (
                    <div key={b.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ width: 9, height: 9, borderRadius: "50%", background: b.color, display: "inline-block" }} />
                        {b.nombre}
                        {b.tipo === "intocable" && <Lock size={11} color={TEXT_MUTED} />}
                      </div>
                      <Money value={val} discreto={discreto} size="sm" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: wideRow ? "repeat(3,1fr)" : "1fr", gap: 14, marginTop: 14 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Últimos movimientos</div>
          {ultimosMovimientos.length === 0 ? <EmptyHint text="Aún no registras movimientos." /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {ultimosMovimientos.map((m) => <MovimientoRow key={m.id} m={m} discreto={discreto} cuentaMap={cuentaMap} bolsaMap={bolsaMap} compact />)}
            </div>
          )}
        </Card>

        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Resumen mensual</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: TEXT_MUTED }}>Ingresos</span>
              <Money value={resumenMensual.ingresos} discreto={discreto} size="base" tone={GREEN} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: TEXT_MUTED }}>Gastos</span>
              <Money value={resumenMensual.gastos} discreto={discreto} size="base" tone={RED} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: TEXT_MUTED }}>Ahorro / Abono a Bolsas</span>
              <Money value={resumenMensual.ahorro} discreto={discreto} size="base" tone={GOLD_LIGHT} />
            </div>
          </div>
        </Card>

        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Evolución del patrimonio</div>
          {historia.length < 2 ? <EmptyHint text="Con más movimientos aquí verás cómo crece tu patrimonio." /> : (
            <div style={{ height: 150 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={historia}>
                  <CartesianGrid stroke={BORDER} vertical={false} />
                  <XAxis dataKey="fecha" tick={{ fill: TEXT_MUTED, fontSize: 10 }} tickFormatter={(v) => v.slice(5)} axisLine={{ stroke: BORDER }} tickLine={false} />
                  <YAxis hide domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ background: PANEL_2, border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 12 }} formatter={(v) => [fmtMoney(v, discreto), "Patrimonio"]} labelStyle={{ color: TEXT_MUTED }} />
                  <Line type="monotone" dataKey="valor" stroke={GOLD} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
      </>
      )}
    </div>
  );
}

function StatCard({ icon: IconEl, color, label, value, discreto, sub }) {
  return (
    <Card style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <IconCircle color={color} size={30}><IconEl size={15} color={color} /></IconCircle>
        <span style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</span>
      </div>
      <Money value={value} discreto={discreto} size="lg" tone={value < 0 ? RED : undefined} />
      <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 3 }}>{sub}</div>
    </Card>
  );
}

/* ============================== MOVIMIENTOS ============================== */

function MovimientoRow({ m, discreto, cuentaMap, bolsaMap, compact, onDelete }) {
  const tipoInfo = TIPOS_MOVIMIENTO.find((t) => t.id === m.tipo);
  const IconEl = tipoInfo?.icon || Repeat;
  const signo = m.tipo === "ingreso" || m.tipo === "trabajo_ingreso" ? "+" : m.tipo === "gasto" || m.tipo === "pago_deuda" || m.tipo === "trabajo_gasto" ? "-" : "";
  let detalle = "";
  if (m.tipo === "ingreso") detalle = [m.origen, cuentaMap[m.cuentaId]].filter(Boolean).join(" · ");
  if (m.tipo === "gasto") detalle = [bolsaMap[m.bolsaId]?.nombre, m.categoria, cuentaMap[m.cuentaId]].filter(Boolean).join(" · ");
  if (m.tipo === "transferencia_bolsas") detalle = `${bolsaMap[m.bolsaId]?.nombre || ""} → ${bolsaMap[m.bolsaDestinoId]?.nombre || ""}`;
  if (m.tipo === "pago_deuda") detalle = `${bolsaMap[m.bolsaId]?.nombre || ""}`;
  if (m.tipo === "trabajo_ingreso" || m.tipo === "trabajo_gasto") detalle = cuentaMap[m.cuentaId] || "";
  if (m.historico) detalle = [detalle, "anterior"].filter(Boolean).join(" · ");

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <IconCircle color={tipoInfo?.tone || GOLD} size={compact ? 30 : 36}>
        <IconEl size={compact ? 14 : 16} color={tipoInfo?.tone || GOLD} />
      </IconCircle>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.concepto || tipoInfo?.label}</div>
        <div style={{ fontSize: 11, color: TEXT_MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{detalle} · {m.fecha}</div>
      </div>
      <Money value={m.importe} discreto={discreto} size="sm" tone={signo === "+" ? GREEN : signo === "-" ? RED : undefined} />
      {!compact && onDelete && (
        <button onClick={() => onDelete(m.id)} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer", padding: 4 }}>
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
}

function MovimientosView({ discreto, movimientos, bolsas, cuentas, onNuevo, onDelete }) {
  const cuentaMap = Object.fromEntries(cuentas.map((c) => [c.id, c.nombre]));
  const bolsaMap = Object.fromEntries(bolsas.map((b) => [b.id, b]));
  const ordenados = [...movimientos].sort((a, b) => (a.fecha < b.fecha ? 1 : -1));

  return (
    <div>
      <ViewHeader title="Movimientos" subtitle="Todo lo que registras queda aquí" onNuevo={onNuevo} nuevoLabel="Nuevo movimiento" />
      {ordenados.length === 0 ? <Card><EmptyHint text="Registra tu primer movimiento para empezar a ver tu historial." /></Card> : (
        <Card>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {ordenados.map((m) => <MovimientoRow key={m.id} m={m} discreto={discreto} cuentaMap={cuentaMap} bolsaMap={bolsaMap} onDelete={onDelete} />)}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ---------- Modal de movimiento ---------- */

function MovimientoModal({ bolsas, cuentas, cuentasTrabajo = [], deudas, deudaPagos, prestamos, prestamoPagos, bolsaSaldos, origenesUsados, categoriasUsadas, onClose, onSave }) {
  const [tipo, setTipo] = useState("ingreso");
  const [importe, setImporte] = useState("");
  const [concepto, setConcepto] = useState("");
  const [origen, setOrigen] = useState("");
  const [categoria, setCategoria] = useState("");
  const [cuentaId, setCuentaId] = useState(cuentas[0]?.id || "");
  const [bolsaId, setBolsaId] = useState(bolsas.find((b) => b.tipo !== "intocable")?.id || bolsas[0]?.id || "");
  const [bolsaDestinoId, setBolsaDestinoId] = useState(bolsas[0]?.id || "");
  const [deudaId, setDeudaId] = useState(deudas[0]?.id || "");
  const [distribuir, setDistribuir] = useState(true);
  const [deudorNombre, setDeudorNombre] = useState("");
  const [fechaCobroEsperada, setFechaCobroEsperada] = useState("");
  const [prestamoId, setPrestamoId] = useState(prestamos?.[0]?.id || "");
  const [signoAjuste, setSignoAjuste] = useState("sumar");
  const [fecha, setFecha] = useState(todayISO());
  const [cuentaTrabajoId, setCuentaTrabajoId] = useState(cuentasTrabajo[0]?.id || "");

  const importeNum = parseFloat(importe) || 0;
  const bolsasSpendable = bolsas.filter((b) => b.tipo !== "intocable");
  const totalPct = bolsas.reduce((acc, b) => acc + (b.porcentaje || 0), 0);

  const distribucion = useMemo(() => {
    if (!distribuir || importeNum <= 0 || totalPct > 100) return [];
    const partes = bolsas.filter((b) => (b.porcentaje || 0) > 0).map((b) => ({ bolsaId: b.id, monto: Math.round((importeNum * (b.porcentaje || 0)) / 100) }));
    const sumaPartes = partes.reduce((a, p) => a + p.monto, 0);
    const resto = importeNum - sumaPartes;
    if (resto > 0) {
      const libre = bolsas.find((b) => b.tipo === "libre");
      if (libre) {
        const idx = partes.findIndex((p) => p.bolsaId === libre.id);
        if (idx >= 0) partes[idx] = { ...partes[idx], monto: partes[idx].monto + resto };
        else partes.push({ bolsaId: libre.id, monto: resto });
      }
    }
    return partes;
  }, [distribuir, importeNum, bolsas, totalPct]);

  const restoADisponible = distribuir && importeNum > 0 && totalPct < 100 ? Math.round((importeNum * (100 - totalPct)) / 100) : 0;

  const bolsaOrigenSaldo = bolsaSaldos[bolsaId] || 0;
  const deudaSeleccionada = deudas.find((d) => d.id === deudaId);
  const deudaSaldo = deudaSeleccionada ? deudaSeleccionada.importeOriginal - (deudaPagos[deudaSeleccionada.id] || 0) : 0;
  const prestamosActivos = (prestamos || []).filter((p) => p.importeOriginal - (prestamoPagos?.[p.id] || 0) > 0);
  const prestamoSeleccionado = prestamosActivos.find((p) => p.id === prestamoId);
  const prestamoSaldo = prestamoSeleccionado ? prestamoSeleccionado.importeOriginal - (prestamoPagos?.[prestamoSeleccionado.id] || 0) : 0;

  const puedeGuardar = () => {
    if (tipo === "ajuste") return importeNum > 0 && !!cuentaId && !!bolsaId;
    if (importeNum <= 0) return false;
    if (tipo === "trabajo_ingreso" || tipo === "trabajo_gasto") return !!cuentaTrabajoId;
    if (tipo === "ingreso") return !!cuentaId && totalPct <= 100;
    if (tipo === "gasto") return !!bolsaId && !!cuentaId && importeNum <= bolsaOrigenSaldo;
    if (tipo === "transferencia_bolsas") return !!bolsaId && !!bolsaDestinoId && bolsaId !== bolsaDestinoId && importeNum <= bolsaOrigenSaldo;
    if (tipo === "pago_deuda") return !!deudaId && !!bolsaId && !!cuentaId && importeNum <= bolsaOrigenSaldo && importeNum <= deudaSaldo;
    if (tipo === "prestamo_otorgado") return !!deudorNombre.trim() && !!bolsaId && !!cuentaId && importeNum <= bolsaOrigenSaldo;
    if (tipo === "cobro_prestamo") return !!prestamoId && !!bolsaId && !!cuentaId && importeNum <= prestamoSaldo;
    if (tipo === "rendimiento") return !!bolsaId;
    return false;
  };

  const guardar = () => {
    const base = { tipo, importe: importeNum, concepto, fecha: fecha || todayISO() };
    if (tipo === "ingreso") {
      onSave({ ...base, cuentaId, origen, distribucion });
    }
    if (tipo === "gasto") onSave({ ...base, cuentaId, bolsaId, categoria });
    if (tipo === "transferencia_bolsas") onSave({ ...base, bolsaId, bolsaDestinoId });
    if (tipo === "pago_deuda") onSave({ ...base, cuentaId, bolsaId, deudaId, concepto: concepto || `Pago a ${deudaSeleccionada?.acreedor || ""}` });
    if (tipo === "prestamo_otorgado") {
      const nuevoId = genId("prs");
      onSave({
        ...base, cuentaId, bolsaId, prestamoId: nuevoId,
        concepto: concepto || `Préstamo a ${deudorNombre}`,
        crearPrestamo: { id: nuevoId, deudor: deudorNombre.trim(), importeOriginal: importeNum, fecha: fecha || todayISO(), fechaCobroEsperada: fechaCobroEsperada || "", estado: "activo" },
      });
    }
    if (tipo === "cobro_prestamo") onSave({ ...base, cuentaId, bolsaId, prestamoId, concepto: concepto || `Cobro a ${prestamoSeleccionado?.deudor || ""}` });
    if (tipo === "rendimiento") onSave({ ...base, bolsaId, concepto: concepto || "Rendimiento de inversión" });
    if (tipo === "ajuste") onSave({ ...base, importe: signoAjuste === "sumar" ? importeNum : -importeNum, cuentaId, bolsaId, concepto: concepto || "Ajuste de conciliación" });
    if (tipo === "trabajo_ingreso" || tipo === "trabajo_gasto") onSave({ ...base, cuentaId: cuentaTrabajoId, concepto: concepto || (tipo === "trabajo_ingreso" ? "Entrada de trabajo" : "Salida de trabajo") });
  };

  return (
    <Modal title="Nuevo movimiento" onClose={onClose} wide>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {TIPOS_MOVIMIENTO.filter((t) => !t.id.startsWith("trabajo_") || cuentasTrabajo.length > 0).map((t) => (
          <button key={t.id} onClick={() => setTipo(t.id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 10, cursor: "pointer", border: `1px solid ${tipo === t.id ? t.tone : BORDER}`, background: tipo === t.id ? `${t.tone}22` : "transparent", color: tipo === t.id ? t.tone : TEXT_MUTED, fontSize: 13, fontWeight: 700 }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <Field label="Importe">
        <TextInput type="number" placeholder="0" value={importe} onChange={(e) => setImporte(e.target.value)} inputMode="decimal" />
      </Field>
      <Field label="Fecha">
        <TextInput type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
      </Field>

      {(tipo === "trabajo_ingreso" || tipo === "trabajo_gasto") && (
        <>
          <Field label="Cuenta de trabajo">
            <Select value={cuentaTrabajoId} onChange={(e) => setCuentaTrabajoId(e.target.value)}>
              {cuentasTrabajo.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          </Field>
          <Field label="Concepto (opcional)">
            <TextInput placeholder="Ej. Anticipo de obra, pago a proveedor" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
          </Field>
          <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 12, fontStyle: "italic" }}>
            Solo es control de tu trabajo: no se reparte en bolsas y no cuenta en tu patrimonio.
          </div>
        </>
      )}

      {tipo === "ingreso" && (
        <>
          <Field label="Origen del ingreso">
            <DataListInput options={uniq([...ORIGENES_INGRESO, ...origenesUsados])} placeholder="Ej. Salario, Negocio, Inversión…" value={origen} onChange={(e) => setOrigen(e.target.value)} />
          </Field>
          <Field label="¿A qué cuenta entró el dinero?">
            <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          </Field>
          <Field label="Concepto (opcional)">
            <TextInput placeholder="Ej. Sueldo de mayo, venta de cámara" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
          </Field>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={distribuir} onChange={(e) => setDistribuir(e.target.checked)} />
            Repartir automáticamente en mis bolsas
          </label>
          {distribuir && totalPct > 100 && (
            <div style={{ fontSize: 12, color: RED, marginBottom: 12, display: "flex", alignItems: "center", gap: 5, background: `${RED}18`, border: `1px solid ${RED}55`, borderRadius: 8, padding: "8px 10px" }}>
              <AlertTriangle size={14} /> Tus porcentajes suman {totalPct}%, más del 100%. Ve a Configuración y corrígelo antes de registrar este ingreso.
            </div>
          )}
          {distribuir && importeNum > 0 && totalPct <= 100 && (
            <div style={{ background: PANEL_2, border: `1px solid ${BORDER}`, borderRadius: 10, padding: 10, marginBottom: 14 }}>
              {distribucion.map((d) => {
                const b = bolsas.find((bb) => bb.id === d.bolsaId);
                const esResto = totalPct < 100 && b?.tipo === "libre";
                return (
                  <div key={d.bolsaId} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0" }}>
                    <span style={{ color: TEXT_MUTED }}>{b?.nombre} ({b?.porcentaje}%{esResto ? " + resto" : ""})</span>
                    <span>{fmtMoney(d.monto, false)}</span>
                  </div>
                );
              })}
              {totalPct < 100 && (
                <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 6, fontStyle: "italic" }}>
                  Solo tienes configurado el {totalPct}% en Configuración — el {100 - totalPct}% restante ({fmtMoney(restoADisponible, false)}) se va automáticamente a Disponible para que ningún peso quede sin bolsa.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tipo === "gasto" && (
        <>
          <Field label="¿De qué bolsa saldrá?">
            <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
              {bolsasSpendable.map((b) => <option key={b.id} value={b.id}>{b.nombre} · disponible {fmtMoney(bolsaSaldos[b.id] || 0, false)}</option>)}
            </Select>
          </Field>
          {importeNum > bolsaOrigenSaldo && (
            <div style={{ fontSize: 12, color: RED, marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
              <AlertTriangle size={13} /> Esta bolsa no tiene fondos suficientes.
            </div>
          )}
          <Field label="¿Desde qué cuenta se pagó?">
            <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          </Field>
          <Field label="Categoría (opcional)">
            <DataListInput options={uniq([...CATEGORIAS_GASTO, ...categoriasUsadas])} placeholder="Ej. Comida, Transporte…" value={categoria} onChange={(e) => setCategoria(e.target.value)} />
          </Field>
          <Field label="Concepto">
            <TextInput placeholder="Ej. Supermercado, gasolina" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
          </Field>
        </>
      )}

      {tipo === "transferencia_bolsas" && (
        <>
          <Field label="Bolsa origen">
            <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
              {bolsasSpendable.map((b) => <option key={b.id} value={b.id}>{b.nombre} · {fmtMoney(bolsaSaldos[b.id] || 0, false)}</option>)}
            </Select>
          </Field>
          <Field label="Bolsa destino">
            <Select value={bolsaDestinoId} onChange={(e) => setBolsaDestinoId(e.target.value)}>
              {bolsas.filter((b) => b.id !== bolsaId).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </Select>
          </Field>
          {importeNum > bolsaOrigenSaldo && (
            <div style={{ fontSize: 12, color: RED, marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
              <AlertTriangle size={13} /> No hay suficiente dinero en esta bolsa.
            </div>
          )}
        </>
      )}

      {tipo === "pago_deuda" && (
        <>
          {deudas.length === 0 ? <div style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 12 }}>Primero registra una deuda en la sección Deudas.</div> : (
            <>
              <Field label="Deuda">
                <Select value={deudaId} onChange={(e) => setDeudaId(e.target.value)}>
                  {deudas.map((d) => <option key={d.id} value={d.id}>{d.acreedor}</option>)}
                </Select>
              </Field>
              <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 10 }}>Saldo pendiente: {fmtMoney(deudaSaldo, false)}</div>
              <Field label="¿De qué bolsa saldrá el pago?">
                <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
                  {bolsasSpendable.map((b) => <option key={b.id} value={b.id}>{b.nombre} · {fmtMoney(bolsaSaldos[b.id] || 0, false)}</option>)}
                </Select>
              </Field>
              <Field label="¿Desde qué cuenta?">
                <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
                  {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </Select>
              </Field>
            </>
          )}
        </>
      )}

      {tipo === "prestamo_otorgado" && (
        <>
          <Field label="¿A quién le prestas?">
            <TextInput placeholder="Ej. Mi primo Luis" value={deudorNombre} onChange={(e) => setDeudorNombre(e.target.value)} />
          </Field>
          <Field label="¿De qué bolsa saldrá?">
            <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
              {bolsasSpendable.map((b) => <option key={b.id} value={b.id}>{b.nombre} · {fmtMoney(bolsaSaldos[b.id] || 0, false)}</option>)}
            </Select>
          </Field>
          {importeNum > bolsaOrigenSaldo && (
            <div style={{ fontSize: 12, color: RED, marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
              <AlertTriangle size={13} /> Esta bolsa no tiene fondos suficientes.
            </div>
          )}
          <Field label="¿Desde qué cuenta?">
            <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          </Field>
          <Field label="¿Cuándo esperas que te pague? (opcional)">
            <TextInput type="date" value={fechaCobroEsperada} onChange={(e) => setFechaCobroEsperada(e.target.value)} />
          </Field>
        </>
      )}

      {tipo === "cobro_prestamo" && (
        <>
          {prestamosActivos.length === 0 ? <div style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 12 }}>No tienes préstamos activos por cobrar.</div> : (
            <>
              <Field label="¿Quién te está pagando?">
                <Select value={prestamoId} onChange={(e) => setPrestamoId(e.target.value)}>
                  {prestamosActivos.map((p) => <option key={p.id} value={p.id}>{p.deudor}</option>)}
                </Select>
              </Field>
              <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 10 }}>Saldo pendiente: {fmtMoney(prestamoSaldo, false)}</div>
              <Field label="¿A qué bolsa entra?">
                <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
                  {bolsasSpendable.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </Select>
              </Field>
              <Field label="¿A qué cuenta entra?">
                <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
                  {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </Select>
              </Field>
            </>
          )}
        </>
      )}

      {tipo === "rendimiento" && (
        <>
          <Field label="¿Qué bolsa de inversión creció?">
            <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
              {bolsas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </Select>
          </Field>
          <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 10, fontStyle: "italic" }}>
            Esto suma valor a la bolsa sin salir de ninguna cuenta — representa una ganancia que todavía no has retirado a un banco.
          </div>
          <Field label="Concepto (opcional)">
            <TextInput placeholder="Ej. Rendimiento del mes, dividendos" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
          </Field>
        </>
      )}

      {tipo === "ajuste" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <button onClick={() => setSignoAjuste("sumar")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: `1px solid ${signoAjuste === "sumar" ? GREEN : BORDER}`, background: signoAjuste === "sumar" ? `${GREEN}22` : "transparent", color: signoAjuste === "sumar" ? GREEN : TEXT_MUTED, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>+ Sumar</button>
            <button onClick={() => setSignoAjuste("restar")} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: `1px solid ${signoAjuste === "restar" ? RED : BORDER}`, background: signoAjuste === "restar" ? `${RED}22` : "transparent", color: signoAjuste === "restar" ? RED : TEXT_MUTED, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>− Restar</button>
          </div>
          <Field label="Cuenta a corregir">
            <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          </Field>
          <Field label="Bolsa a corregir">
            <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
              {bolsas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </Select>
          </Field>
          <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 10, fontStyle: "italic" }}>
            Úsalo cuando el saldo real de tu banco no coincida con lo que calcula la app (por ejemplo, una comisión que no habías registrado).
          </div>
          <Field label="Concepto">
            <TextInput placeholder="Ej. Comisión bancaria no registrada" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
          </Field>
        </>
      )}

      <PrimaryButton disabled={!puedeGuardar()} onClick={guardar} style={{ marginTop: 6 }}>Registrar movimiento</PrimaryButton>
    </Modal>
  );
}

/* ============================== BOLSAS ============================== */

function SectionGroupHeader({ label, count }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 800, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: 0.5, margin: "18px 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
      {label}
      <span style={{ flex: 1, height: 1, background: BORDER }} />
      <span style={{ fontWeight: 600 }}>{count}</span>
    </div>
  );
}

function BolsasView({ discreto, bolsas, bolsaSaldos, onNueva, onDelete, onEdit, cols2 }) {
  const grupos = [
    { id: "libre", label: "Disponible" },
    { id: "intocable", label: "Intocables" },
    { id: "meta", label: "Metas" },
    { id: "inversion", label: "Inversiones" },
    { id: "normal", label: "Bolsas normales" },
  ];

  const renderBolsa = (b) => {
    const saldo = bolsaSaldos[b.id] || 0;
    const progreso = b.tipo === "meta" && b.objetivo > 0 ? Math.min((saldo / b.objetivo) * 100, 100) : null;
    return (
      <Card key={b.id}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <IconCircle color={b.color} size={36}><PiggyBank size={17} color={b.color} /></IconCircle>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 5 }}>
                {b.nombre}
                {b.tipo === "intocable" && <Lock size={12} color={TEXT_MUTED} />}
              </div>
              <div style={{ fontSize: 11, color: TEXT_MUTED }}>{b.porcentaje || 0}% de ingresos</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => onEdit(b)} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}><Settings size={15} /></button>
            {b.tipo !== "libre" && (
              <button onClick={() => (saldo === 0 ? onDelete(b.id) : alert("Primero mueve el dinero de esta bolsa a otra."))} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}><Trash2 size={15} /></button>
            )}
          </div>
        </div>
        <div style={{ marginTop: 14 }}><Money value={saldo} discreto={discreto} size="lg" /></div>
        {progreso !== null && (
          <div style={{ marginTop: 10 }}>
            <div style={{ height: 6, background: PANEL_2, borderRadius: 6, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${progreso}%`, background: b.color }} />
            </div>
            <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 4 }}>{progreso.toFixed(0)}% de {fmtMoney(b.objetivo, discreto)}</div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div>
      <ViewHeader title="Bolsas" subtitle="Cada peso tiene un propósito — agrupadas por tipo" onNuevo={onNueva} nuevoLabel="Nueva bolsa" />
      {grupos.map((g) => {
        const items = bolsas.filter((b) => b.tipo === g.id);
        if (items.length === 0) return null;
        return (
          <div key={g.id}>
            <SectionGroupHeader label={g.label} count={items.length} />
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols2},1fr)`, gap: 12 }}>
              {items.map(renderBolsa)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BolsaModal({ bolsa, onClose, onSave, forceMeta, otrosPct }) {
  const [nombre, setNombre] = useState(bolsa?.nombre || "");
  const [tipo, setTipo] = useState(bolsa?.tipo || "normal");
  const [objetivo, setObjetivo] = useState(bolsa?.objetivo || "");
  const [color, setColor] = useState(bolsa?.color || BOLSA_COLORS[Math.floor(Math.random() * BOLSA_COLORS.length)]);
  const [porcentaje, setPorcentaje] = useState(bolsa?.porcentaje ?? 0);

  const guardar = () => {
    if (!nombre.trim()) return;
    onSave({ nombre: nombre.trim(), tipo, objetivo: parseFloat(objetivo) || 0, color, porcentaje: parseFloat(porcentaje) || 0 });
  };

  return (
    <Modal title={forceMeta ? "Nueva meta" : bolsa?.id ? "Editar bolsa" : "Nueva bolsa"} onClose={onClose}>
      <Field label="Nombre">
        <TextInput placeholder={forceMeta ? "Ej. Viaje a Japón" : "Ej. Colegiaturas"} value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </Field>
      {!forceMeta && (
        <Field label="Tipo">
          <Select value={tipo} onChange={(e) => setTipo(e.target.value)} disabled={bolsa?.tipo === "libre"}>
            <option value="normal">Normal</option>
            <option value="meta">Meta (proyecto con objetivo)</option>
            <option value="inversion">Inversión</option>
            <option value="intocable">Intocable (no se puede gastar)</option>
          </Select>
        </Field>
      )}
      {(tipo === "meta" || forceMeta) && (
        <Field label="Objetivo">
          <TextInput type="number" placeholder="0" value={objetivo} onChange={(e) => setObjetivo(e.target.value)} />
        </Field>
      )}
      <Field label={`% automático de cada ingreso: ${porcentaje}%`}>
        <input type="range" min="0" max="100" value={porcentaje} onChange={(e) => setPorcentaje(e.target.value)} style={{ width: "100%" }} />
      </Field>
      {otrosPct !== undefined && (() => {
        const tot = otrosPct + (parseFloat(porcentaje) || 0);
        return (
          <div style={{ fontSize: 12, marginBottom: 12, color: tot === 100 ? GREEN : tot > 100 ? RED : YELLOW }}>
            Con este porcentaje tus bolsas suman {tot}%{tot < 100 ? ` — el ${100 - tot}% restante se va a Disponible` : tot > 100 ? " — te pasaste de 100%" : ""}.
          </div>
        );
      })()}
      <Field label="Color">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {BOLSA_COLORS.map((c) => (
            <div key={c} onClick={() => setColor(c)} style={{ width: 28, height: 28, borderRadius: "50%", background: c, cursor: "pointer", border: color === c ? `2px solid #fff` : "2px solid transparent" }} />
          ))}
        </div>
      </Field>
      <PrimaryButton onClick={guardar} disabled={!nombre.trim()} style={{ marginTop: 6 }}>Guardar</PrimaryButton>
    </Modal>
  );
}

/* ============================== METAS ============================== */

function MetasView({ discreto, bolsas, bolsaSaldos, onNueva, cols2 }) {
  const metas = bolsas.filter((b) => b.tipo === "meta");
  return (
    <div>
      <ViewHeader title="Metas" subtitle="Tus proyectos con objetivo claro" onNuevo={onNueva} nuevoLabel="Nueva meta" />
      {metas.length === 0 ? <Card><EmptyHint text="Crea una meta —por ejemplo un viaje o un proyecto— y aparecerá aquí con su avance." /></Card> : (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols2},1fr)`, gap: 12 }}>
          {metas.map((b) => {
            const saldo = bolsaSaldos[b.id] || 0;
            const progreso = b.objetivo > 0 ? Math.min((saldo / b.objetivo) * 100, 100) : 0;
            return (
              <Card key={b.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <IconCircle color={b.color} size={38}><Target size={18} color={b.color} /></IconCircle>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{b.nombre}</div>
                </div>
                <Money value={saldo} discreto={discreto} size="lg" />
                <div style={{ height: 8, background: PANEL_2, borderRadius: 6, overflow: "hidden", marginTop: 10 }}>
                  <div style={{ height: "100%", width: `${progreso}%`, background: b.color }} />
                </div>
                <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 6 }}>{progreso.toFixed(0)}% de la meta ({fmtMoney(b.objetivo, discreto)})</div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================== CUENTAS ============================== */

function computeHistorialCuenta(cuentaId, movimientos) {
  const relevantes = movimientos
    .filter((m) => ["ingreso", "gasto", "pago_deuda", "prestamo_otorgado", "cobro_prestamo", "ajuste", "trabajo_ingreso", "trabajo_gasto"].includes(m.tipo) && m.cuentaId === cuentaId && !m.historico)
    .sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0));
  let saldo = 0;
  const conSaldo = relevantes.map((m) => {
    const signo = m.tipo === "ingreso" || m.tipo === "cobro_prestamo" || m.tipo === "trabajo_ingreso" ? 1 : m.tipo === "ajuste" ? Math.sign(m.importe) || 1 : -1;
    saldo += m.tipo === "ajuste" ? m.importe : signo * m.importe;
    return { ...m, saldoDespues: saldo };
  });
  return conSaldo.reverse();
}

function CuentasView({ discreto, cuentas, cuentaSaldos, movimientos, bolsas, onNueva, onDelete, cols2 }) {
  const [verHistorial, setVerHistorial] = useState(null);
  const tiposTodos = uniq(CUENTA_TIPOS.concat(cuentas.map((c) => c.tipo)));
  const grupos = CUENTA_CLASES.flatMap((cl) => tiposTodos.map((t) => ({ clase: cl.id, tipo: t })));
  const bolsaMap = Object.fromEntries((bolsas || []).map((b) => [b.id, b]));

  return (
    <div>
      <ViewHeader title="Cuentas" subtitle="Patrimonio: dinero tuyo que se reparte en bolsas · Trabajo: solo control, no se reparte" onNuevo={onNueva} nuevoLabel="Nueva cuenta" />
      {grupos.map((g) => {
        const tipo = g.tipo;
        const items = cuentas.filter((c) => c.tipo === tipo && (c.clase || "patrimonio") === g.clase);
        if (items.length === 0) return null;
        return (
          <div key={g.clase + tipo}>
            <SectionGroupHeader label={g.clase === "trabajo" ? `Trabajo (solo control) · ${tipo}` : tipo} count={items.length} />
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols2},1fr)`, gap: 12 }}>
              {items.map((c) => (
                <Card key={c.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <IconCircle color={GOLD} size={36}><Building2 size={17} color={GOLD} /></IconCircle>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{c.nombre}</div>
                        <div style={{ fontSize: 11, color: TEXT_MUTED }}>{c.tipo}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => setVerHistorial(c)} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}><History size={15} /></button>
                      <button onClick={() => ((cuentaSaldos[c.id] || 0) === 0 ? onDelete(c.id) : alert("Esta cuenta todavía tiene saldo."))} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  <div style={{ marginTop: 14 }}><Money value={cuentaSaldos[c.id] || 0} discreto={discreto} size="lg" /></div>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {verHistorial && (
        <Modal title={`Historial · ${verHistorial.nombre}`} onClose={() => setVerHistorial(null)}>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 12 }}>Saldo actual: <Money value={cuentaSaldos[verHistorial.id] || 0} discreto={discreto} size="sm" /></div>
          {(() => {
            const hist = computeHistorialCuenta(verHistorial.id, movimientos);
            if (hist.length === 0) return <EmptyHint text="Sin movimientos todavía en esta cuenta." />;
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {hist.map((m) => {
                  const tipoInfo = TIPOS_MOVIMIENTO.find((t) => t.id === m.tipo);
                  return (
                    <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${BORDER}`, paddingBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{m.concepto || tipoInfo?.label}</div>
                        <div style={{ fontSize: 11, color: TEXT_MUTED }}>{m.fecha} · {tipoInfo?.label}{m.bolsaId ? ` · ${bolsaMap[m.bolsaId]?.nombre || ""}` : ""}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <Money value={m.tipo === "ajuste" ? m.importe : (m.tipo === "ingreso" || m.tipo === "cobro_prestamo" || m.tipo === "trabajo_ingreso" ? m.importe : -m.importe)} discreto={discreto} size="sm" tone={(m.tipo === "ajuste" ? m.importe : (m.tipo === "ingreso" || m.tipo === "cobro_prestamo" || m.tipo === "trabajo_ingreso" ? m.importe : -m.importe)) >= 0 ? GREEN : RED} />
                        <div style={{ fontSize: 10, color: TEXT_MUTED }}>saldo: {fmtMoney(m.saldoDespues, discreto)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
}

function CuentaModal({ onClose, onSave, bancosUsados }) {
  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState("Banco");
  const [clase, setClase] = useState("patrimonio");
  const sugerencias = uniq([...BANCOS_SUGERIDOS, ...bancosUsados]);
  return (
    <Modal title="Nueva cuenta" onClose={onClose}>
      <Field label="Nombre del banco o billetera">
        <DataListInput options={sugerencias} placeholder="Escribe o elige de la lista: BBVA, Nu, Efectivo…" value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </Field>
      <Field label="Tipo">
        <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {CUENTA_TIPOS.map((t) => <option key={t}>{t}</option>)}
        </Select>
      </Field>
      <Field label="¿Para qué es esta cuenta?">
        <Select value={clase} onChange={(e) => setClase(e.target.value)}>
          <option value="patrimonio">Mi patrimonio (se reparte en bolsas)</option>
          <option value="trabajo">Control de mi trabajo (solo informativa)</option>
        </Select>
      </Field>
      <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 14, fontStyle: "italic" }}>
        Las tarjetas de crédito y otras deudas se registran en la sección "Deudas", no aquí — las cuentas son solo dinero que es tuyo.
      </div>
      <PrimaryButton onClick={() => nombre.trim() && onSave({ nombre: nombre.trim(), tipo, clase })} disabled={!nombre.trim()}>Guardar cuenta</PrimaryButton>
    </Modal>
  );
}

/* ============================== DEUDAS ============================== */

function computeHistorialDeuda(deudaId, movimientos) {
  return movimientos
    .filter((m) => m.tipo === "pago_deuda" && m.deudaId === deudaId)
    .sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
}

function DeudasView({ discreto, deudas, deudaPagos, movimientos, cuentas, bolsas, onNueva, onDelete, onAbonar }) {
  const [verHistorial, setVerHistorial] = useState(null);
  const conSaldo = deudas.map((d) => ({ ...d, saldo: Math.max(d.importeOriginal - (deudaPagos[d.id] || 0), 0) }));
  const cuentaMap = Object.fromEntries((cuentas || []).map((c) => [c.id, c.nombre]));
  const bolsaMap = Object.fromEntries((bolsas || []).map((b) => [b.id, b.nombre]));

  const renderDeuda = (d) => {
    const dias = daysUntil(d.fechaPago);
    const pagado = d.importeOriginal - d.saldo;
    const progreso = d.importeOriginal > 0 ? (pagado / d.importeOriginal) * 100 : 0;
    const urgencia = d.saldo <= 0 ? GREEN : dias < 0 ? RED : dias <= 5 ? YELLOW : TEXT_MUTED;
    return (
      <Card key={d.id}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{d.acreedor}</div>
            <div style={{ fontSize: 12, color: urgencia, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
              {d.saldo <= 0 ? <><Check size={12} /> Liquidada</> : dias < 0 ? <><AlertTriangle size={12} /> Vencida hace {Math.abs(dias)} días</> : `Vence en ${dias} días`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => setVerHistorial(d)} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}><History size={15} /></button>
            <button onClick={() => onDelete(d.id)} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}><Trash2 size={15} /></button>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 12 }}>
          <Money value={d.saldo} discreto={discreto} size="lg" tone={d.saldo > 0 ? RED : GREEN} />
          <span style={{ fontSize: 11, color: TEXT_MUTED }}>de {fmtMoney(d.importeOriginal, discreto)}</span>
        </div>
        <div style={{ height: 6, background: PANEL_2, borderRadius: 6, overflow: "hidden", marginTop: 8 }}>
          <div style={{ height: "100%", width: `${progreso}%`, background: GOLD }} />
        </div>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: TEXT_MUTED }}>
            {d.mensualidad > 0
              ? `Cuota ${fmtMoney(d.mensualidad, discreto)} · ${d.frecuencia || "Mensual"}${d.saldo > 0 ? ` · faltan ≈ ${Math.ceil(d.saldo / d.mensualidad)} pagos` : ""}`
              : d.pagoMinimo > 0 ? `Pago mínimo ${fmtMoney(d.pagoMinimo, discreto)}` : ""}
            {d.saldo > 0 && ` · próximo pago: ${d.fechaPago}`}
          </span>
          {d.saldo > 0 && <button onClick={() => onAbonar(d)} style={BTN_SMALL}>Abonar</button>}
        </div>
        {(() => {
          const ult = computeHistorialDeuda(d.id, movimientos).slice(0, 3);
          if (ult.length === 0) return null;
          return (
            <div style={{ marginTop: 10, borderTop: `1px solid ${BORDER}`, paddingTop: 8 }}>
              <div style={{ fontSize: 10, color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Últimos pagos</div>
              {ult.map((m) => (
                <div key={m.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0" }}>
                  <span style={{ color: TEXT_MUTED }}>{m.fecha}{m.historico ? " · anterior" : ""}</span>
                  <span>{fmtMoney(m.importe, discreto)}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </Card>
    );
  };

  return (
    <div>
      <ViewHeader title="Deudas" subtitle="Lo que debes y cuándo pagarlo — agrupadas por tipo" onNuevo={onNueva} nuevoLabel="Nueva deuda" />
      {conSaldo.length === 0 ? <Card><EmptyHint text="No tienes deudas registradas." /></Card> : (
        DEUDA_TIPOS.map((grupo) => {
          const items = conSaldo.filter((d) => (d.tipo || "otro") === grupo.id);
          if (items.length === 0) return null;
          return (
            <div key={grupo.id}>
              <SectionGroupHeader label={grupo.label} count={items.length} />
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{items.map(renderDeuda)}</div>
            </div>
          );
        })
      )}

      {verHistorial && (
        <Modal title={`Historial de pagos · ${verHistorial.acreedor}`} onClose={() => setVerHistorial(null)}>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 12 }}>
            Saldo pendiente: <Money value={Math.max(verHistorial.importeOriginal - (deudaPagos[verHistorial.id] || 0), 0)} discreto={discreto} size="sm" tone={RED} />
          </div>
          {(() => {
            const hist = computeHistorialDeuda(verHistorial.id, movimientos);
            if (hist.length === 0) return <EmptyHint text="Todavía no registras pagos de esta deuda." />;
            let acumulado = 0;
            const conRestante = hist.slice().reverse().map((m) => { acumulado += m.importe; return { ...m, restante: verHistorial.importeOriginal - acumulado }; }).reverse();
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {conRestante.map((m) => (
                  <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${BORDER}`, paddingBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{m.fecha}</div>
                      <div style={{ fontSize: 11, color: TEXT_MUTED }}>{cuentaMap[m.cuentaId] || ""} · {bolsaMap[m.bolsaId] || ""}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Money value={m.importe} discreto={discreto} size="sm" tone={GREEN} />
                      <div style={{ fontSize: 10, color: TEXT_MUTED }}>restante: {fmtMoney(Math.max(m.restante, 0), discreto)}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
}

function DeudaModal({ onClose, onSave }) {
  const [acreedor, setAcreedor] = useState("");
  const [tipo, setTipo] = useState("tarjeta_credito");
  const [importeOriginal, setImporteOriginal] = useState("");
  const [fechaPago, setFechaPago] = useState(todayISO());
  const [fechaPagoEditada, setFechaPagoEditada] = useState(false);
  const [pagoMinimo, setPagoMinimo] = useState("");
  const [mensualidad, setMensualidad] = useState("");
  const [frecuencia, setFrecuencia] = useState("Mensual");
  const [numPagos, setNumPagos] = useState("");
  const [hechos, setHechos] = useState("");
  const [primerPago, setPrimerPago] = useState("");
  const [montoHecho, setMontoHecho] = useState("");

  const esPlan = tipo !== "tarjeta_credito";
  const mensNum = parseFloat(mensualidad) || 0;
  const nPagosNum = parseInt(numPagos) || 0;
  const hechosNum = esPlan ? Math.max(parseInt(hechos) || 0, 0) : 0;
  const montoHechoNum = parseFloat(montoHecho) || mensNum;
  const importeNum = parseFloat(importeOriginal) || (mensNum && nPagosNum ? mensNum * nPagosNum : 0);
  const totalHecho = hechosNum * montoHechoNum;

  // Si ya hiciste pagos antes, sugerimos la próxima fecha a partir del primero (puedes cambiarla).
  useEffect(() => {
    if (fechaPagoEditada || !esPlan || hechosNum <= 0 || !primerPago) return;
    setFechaPago(addPeriodo(primerPago, frecuencia, hechosNum));
  }, [hechosNum, primerPago, frecuencia, esPlan, fechaPagoEditada]);

  const excede = importeNum > 0 && totalHecho > importeNum;
  const puede = !!acreedor.trim() && importeNum > 0 && !excede && (hechosNum === 0 || (!!primerPago && montoHechoNum > 0));

  const guardar = () => {
    if (!puede) return;
    const historicos = [];
    for (let i = 0; i < hechosNum; i++) {
      historicos.push({ importe: montoHechoNum, fecha: addPeriodo(primerPago, frecuencia, i), concepto: `Pago ${i + 1} (anterior) · ${acreedor.trim()}`, cuentaId: "", bolsaId: "" });
    }
    onSave({
      acreedor: acreedor.trim(), tipo, importeOriginal: importeNum, fechaPago, estado: "activa",
      pagoMinimo: esPlan ? 0 : parseFloat(pagoMinimo) || 0,
      mensualidad: esPlan ? mensNum : 0,
      frecuencia: esPlan ? frecuencia : "Mensual",
      numPagos: esPlan ? nPagosNum : 0,
    }, historicos);
  };

  return (
    <Modal title="Nueva deuda" onClose={onClose}>
      <Field label="Tipo de deuda">
        <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {DEUDA_TIPOS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </Select>
      </Field>
      <Field label="¿A quién le debes?">
        <TextInput placeholder={esPlan ? "Ej. Crédito del auto" : "Ej. Tarjeta de crédito BBVA"} value={acreedor} onChange={(e) => setAcreedor(e.target.value)} />
      </Field>
      <Field label={esPlan ? "Monto total a pagar (incluye lo que ya pagaste)" : "Saldo actual de la tarjeta"}>
        <TextInput type="number" placeholder="0" value={importeOriginal} onChange={(e) => setImporteOriginal(e.target.value)} />
      </Field>

      {esPlan ? (
        <>
          <Field label="Mensualidad o cuota">
            <TextInput type="number" placeholder="0" value={mensualidad} onChange={(e) => setMensualidad(e.target.value)} />
          </Field>
          <Field label="Cada cuándo pagas">
            <Select value={frecuencia} onChange={(e) => setFrecuencia(e.target.value)}>
              {FRECUENCIAS.filter((f) => f !== "Único").map((f) => <option key={f} value={f}>{f}</option>)}
            </Select>
          </Field>
          <Field label="Número total de pagos (opcional)">
            <TextInput type="number" placeholder="Ej. 48" value={numPagos} onChange={(e) => setNumPagos(e.target.value)} />
          </Field>
          {!importeOriginal && mensNum > 0 && nPagosNum > 0 && (
            <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 10, fontStyle: "italic" }}>
              Monto total calculado: {fmtMoney(mensNum * nPagosNum, false)} ({nPagosNum} pagos de {fmtMoney(mensNum, false)}).
            </div>
          )}

          <div style={{ background: PANEL_2, border: `1px solid ${BORDER}`, borderRadius: 10, padding: 12, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: GOLD_LIGHT, marginBottom: 4 }}>¿Ya llevas pagos hechos?</div>
            <div style={{ fontSize: 11, color: TEXT_MUTED, marginBottom: 10, lineHeight: 1.5 }}>
              Se registran como pagos anteriores: bajan el saldo de la deuda pero NO mueven tus cuentas ni tus bolsas de hoy.
            </div>
            <Field label="¿Cuántos pagos has hecho?">
              <TextInput type="number" placeholder="0" value={hechos} onChange={(e) => setHechos(e.target.value)} />
            </Field>
            {hechosNum > 0 && (
              <>
                <Field label="Fecha del primer pago que hiciste">
                  <TextInput type="date" value={primerPago} onChange={(e) => setPrimerPago(e.target.value)} />
                </Field>
                <Field label="Monto de cada pago (si fue la cuota, déjalo vacío)">
                  <TextInput type="number" placeholder={mensNum ? String(mensNum) : "0"} value={montoHecho} onChange={(e) => setMontoHecho(e.target.value)} />
                </Field>
                {excede && (
                  <div style={{ fontSize: 12, color: RED, marginBottom: 6 }}>
                    Esos pagos ({fmtMoney(totalHecho, false)}) suman más que el monto total de la deuda.
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        <Field label="Pago mínimo (opcional)">
          <TextInput type="number" placeholder="0" value={pagoMinimo} onChange={(e) => setPagoMinimo(e.target.value)} />
        </Field>
      )}

      <Field label={esPlan ? "Próxima fecha de pago" : "Próxima fecha de pago (se recorre cada mes al abonar)"}>
        <TextInput type="date" value={fechaPago} onChange={(e) => { setFechaPago(e.target.value); setFechaPagoEditada(true); }} />
      </Field>
      <PrimaryButton onClick={guardar} disabled={!puede}>Guardar deuda</PrimaryButton>
    </Modal>
  );
}

function AbonoDeudaModal({ deuda, saldo, cuentas, bolsas, bolsaSaldos, onClose, onSave }) {
  const bolsasSp = bolsas.filter((b) => b.tipo !== "intocable");
  const [importe, setImporte] = useState(String(deuda.mensualidad || deuda.pagoMinimo || ""));
  const [fecha, setFecha] = useState(todayISO());
  const [bolsaId, setBolsaId] = useState(bolsasSp.find((b) => b.tipo === "libre")?.id || bolsasSp[0]?.id || "");
  const [cuentaId, setCuentaId] = useState(cuentas[0]?.id || "");
  const [anterior, setAnterior] = useState(false);
  const [avanzar, setAvanzar] = useState(true);
  const [proxima, setProxima] = useState(addPeriodo(deuda.fechaPago, deuda.frecuencia || "Mensual"));

  const importeNum = parseFloat(importe) || 0;
  const bolsaSaldo = bolsaSaldos[bolsaId] || 0;
  const puede = importeNum > 0 && importeNum <= saldo && (anterior || (!!bolsaId && !!cuentaId && importeNum <= bolsaSaldo));

  const guardar = () => {
    if (!puede) return;
    onSave(
      { importe: importeNum, fecha: fecha || todayISO(), cuentaId: anterior ? "" : cuentaId, bolsaId: anterior ? "" : bolsaId, concepto: `Pago a ${deuda.acreedor}`, ...(anterior ? { historico: true } : {}) },
      avanzar ? proxima : null
    );
  };

  return (
    <Modal title={`Abonar · ${deuda.acreedor}`} onClose={onClose}>
      <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 12 }}>Saldo pendiente: {fmtMoney(saldo, false)}</div>
      <Field label="Monto del abono">
        <TextInput type="number" placeholder="0" value={importe} onChange={(e) => setImporte(e.target.value)} inputMode="decimal" />
      </Field>
      {importeNum > saldo && <div style={{ fontSize: 12, color: RED, marginBottom: 10 }}>El abono es mayor que lo que falta por pagar.</div>}
      <Field label="Fecha del pago">
        <TextInput type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
      </Field>
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, marginBottom: 12, cursor: "pointer", lineHeight: 1.4 }}>
        <input type="checkbox" checked={anterior} onChange={(e) => { setAnterior(e.target.checked); setAvanzar(!e.target.checked); }} style={{ marginTop: 3 }} />
        <span>Es un pago que ya hice antes <span style={{ color: TEXT_MUTED }}>(baja el saldo, pero no mueve mis cuentas ni bolsas)</span></span>
      </label>
      {!anterior && (
        <>
          <Field label="¿De qué bolsa sale?">
            <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
              {bolsasSp.map((b) => <option key={b.id} value={b.id}>{b.nombre} · {fmtMoney(bolsaSaldos[b.id] || 0, false)}</option>)}
            </Select>
          </Field>
          {importeNum > bolsaSaldo && <div style={{ fontSize: 12, color: RED, marginBottom: 10 }}>Esa bolsa no tiene fondos suficientes.</div>}
          <Field label="¿De qué cuenta sale el dinero?">
            <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          </Field>
        </>
      )}
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 8, cursor: "pointer" }}>
        <input type="checkbox" checked={avanzar} onChange={(e) => setAvanzar(e.target.checked)} />
        Actualizar la próxima fecha de pago
      </label>
      {avanzar && (
        <Field label="Próxima fecha de pago">
          <TextInput type="date" value={proxima} onChange={(e) => setProxima(e.target.value)} />
        </Field>
      )}
      <PrimaryButton onClick={guardar} disabled={!puede} style={{ marginTop: 6 }}>Registrar abono</PrimaryButton>
    </Modal>
  );
}

function CobroPrestamoModal({ prestamo, saldo, cuentas, bolsas, onClose, onSave }) {
  const bolsasSp = bolsas.filter((b) => b.tipo !== "intocable");
  const [importe, setImporte] = useState("");
  const [fecha, setFecha] = useState(todayISO());
  const [bolsaId, setBolsaId] = useState(bolsasSp.find((b) => b.tipo === "libre")?.id || bolsasSp[0]?.id || "");
  const [cuentaId, setCuentaId] = useState(cuentas[0]?.id || "");
  const [anterior, setAnterior] = useState(false);
  const [proxima, setProxima] = useState(prestamo.fechaCobroEsperada || "");

  const importeNum = parseFloat(importe) || 0;
  const puede = importeNum > 0 && importeNum <= saldo && (anterior || (!!bolsaId && !!cuentaId));

  const guardar = () => {
    if (!puede) return;
    onSave(
      { importe: importeNum, fecha: fecha || todayISO(), cuentaId: anterior ? "" : cuentaId, bolsaId: anterior ? "" : bolsaId, concepto: `Cobro a ${prestamo.deudor}`, ...(anterior ? { historico: true } : {}) },
      proxima
    );
  };

  return (
    <Modal title={`Cobro · ${prestamo.deudor}`} onClose={onClose}>
      <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 12 }}>Te falta cobrar: {fmtMoney(saldo, false)}</div>
      <Field label="Monto que te pagaron">
        <TextInput type="number" placeholder="0" value={importe} onChange={(e) => setImporte(e.target.value)} inputMode="decimal" />
      </Field>
      {importeNum > saldo && <div style={{ fontSize: 12, color: RED, marginBottom: 10 }}>Es más de lo que te debe.</div>}
      <Field label="Fecha del cobro">
        <TextInput type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
      </Field>
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, marginBottom: 12, cursor: "pointer", lineHeight: 1.4 }}>
        <input type="checkbox" checked={anterior} onChange={(e) => setAnterior(e.target.checked)} style={{ marginTop: 3 }} />
        <span>Es un cobro que ya recibí antes <span style={{ color: TEXT_MUTED }}>(baja lo que te deben, pero no mueve mis cuentas ni bolsas)</span></span>
      </label>
      {!anterior && (
        <>
          <Field label="¿A qué cuenta entra el dinero?">
            <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          </Field>
          <Field label="¿A qué bolsa entra?">
            <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
              {bolsasSp.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </Select>
          </Field>
        </>
      )}
      <Field label="Próxima fecha esperada de cobro (opcional)">
        <TextInput type="date" value={proxima} onChange={(e) => setProxima(e.target.value)} />
      </Field>
      <PrimaryButton onClick={guardar} disabled={!puede} style={{ marginTop: 6 }}>Registrar cobro</PrimaryButton>
    </Modal>
  );
}

function PagarProgramadoModal({ pago, cuentas, bolsas, bolsaSaldos, categoriasUsadas, onClose, onSave }) {
  const bolsasSp = bolsas.filter((b) => b.tipo !== "intocable");
  const [importe, setImporte] = useState(String(pago.ultimoMonto || pago.importe || ""));
  const [fecha, setFecha] = useState(todayISO());
  const [bolsaId, setBolsaId] = useState(bolsasSp.find((b) => b.id === pago.bolsaId)?.id || bolsasSp.find((b) => b.tipo === "libre")?.id || bolsasSp[0]?.id || "");
  const [cuentaId, setCuentaId] = useState(cuentas.find((c) => c.id === pago.cuentaId)?.id || cuentas[0]?.id || "");
  const [categoria, setCategoria] = useState("Servicios");
  const [anterior, setAnterior] = useState(false);
  const unico = pago.frecuencia === "Único";
  const [proxima, setProxima] = useState(unico ? "" : addPeriodo(pago.fechaProxima, pago.frecuencia));

  const importeNum = parseFloat(importe) || 0;
  const bolsaSaldo = bolsaSaldos[bolsaId] || 0;
  const puede = importeNum > 0 && (anterior || (!!bolsaId && !!cuentaId && importeNum <= bolsaSaldo));

  const guardar = () => {
    if (!puede) return;
    onSave(
      { importe: importeNum, fecha: fecha || todayISO(), cuentaId, bolsaId, categoria: categoria.trim() || "Servicios", concepto: pago.nombre, ...(anterior ? { historico: true } : {}) },
      anterior || unico ? null : proxima,
      !anterior && unico
    );
  };

  return (
    <Modal title={`Pagar · ${pago.nombre}`} onClose={onClose}>
      <Field label="¿Cuánto pagaste esta vez?">
        <TextInput type="number" placeholder="0" value={importe} onChange={(e) => setImporte(e.target.value)} inputMode="decimal" />
      </Field>
      <div style={{ fontSize: 11, color: TEXT_MUTED, margin: "-6px 0 12px", fontStyle: "italic" }}>
        Ya viene con el monto del último pago; cámbialo si el recibo salió distinto.
      </div>
      <Field label="Fecha del pago">
        <TextInput type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
      </Field>
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, marginBottom: 12, cursor: "pointer", lineHeight: 1.4 }}>
        <input type="checkbox" checked={anterior} onChange={(e) => setAnterior(e.target.checked)} style={{ marginTop: 3 }} />
        <span>Es un recibo de un mes anterior <span style={{ color: TEXT_MUTED }}>(solo alimenta la gráfica: no mueve cuentas ni bolsas ni cambia la próxima fecha)</span></span>
      </label>
      {!anterior && (
        <>
          <Field label="¿De qué bolsa sale?">
            <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
              {bolsasSp.map((b) => <option key={b.id} value={b.id}>{b.nombre} · {fmtMoney(bolsaSaldos[b.id] || 0, false)}</option>)}
            </Select>
          </Field>
          {importeNum > bolsaSaldo && <div style={{ fontSize: 12, color: RED, marginBottom: 10 }}>Esa bolsa no tiene fondos suficientes.</div>}
          <Field label="¿De qué cuenta sale?">
            <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          </Field>
        </>
      )}
      <Field label="Categoría">
        <DataListInput options={uniq([...CATEGORIAS_GASTO, ...categoriasUsadas])} value={categoria} onChange={(e) => setCategoria(e.target.value)} />
      </Field>
      {!anterior && !unico && (
        <Field label="Próxima fecha de este pago">
          <TextInput type="date" value={proxima} onChange={(e) => setProxima(e.target.value)} />
        </Field>
      )}
      <PrimaryButton onClick={guardar} disabled={!puede} style={{ marginTop: 6 }}>Registrar pago</PrimaryButton>
    </Modal>
  );
}

function GraficaPagosProgramados({ pagos, movimientos, discreto }) {
  const [sel, setSel] = useState("todos");
  const hoy = new Date();
  const meses = [];
  for (let i = 11; i >= 0; i--) {
    const f = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
    meses.push(`${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}`);
  }
  const ligados = movimientos.filter((m) => m.tipo === "gasto" && m.pagoProgramadoId && (sel === "todos" || m.pagoProgramadoId === sel));
  const datos = meses.map((mes) => ({
    mes,
    etiqueta: MESES_CORTOS[Number(mes.slice(5)) - 1],
    total: ligados.filter((m) => (m.fecha || "").startsWith(mes)).reduce((a, m) => a + m.importe, 0),
  }));
  const conPago = datos.filter((d) => d.total > 0);
  const promedio = conPago.length ? conPago.reduce((a, d) => a + d.total, 0) / conPago.length : 0;
  const caro = conPago.reduce((a, d) => (!a || d.total > a.total ? d : a), null);
  const barato = conPago.reduce((a, d) => (!a || d.total < a.total ? d : a), null);
  const totalAnio = conPago.reduce((a, d) => a + d.total, 0);

  return (
    <div style={{ marginTop: 20 }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Comportamiento de tus pagos · últimos 12 meses</div>
          <Select value={sel} onChange={(e) => setSel(e.target.value)} style={{ maxWidth: 220 }}>
            <option value="todos">Todos los pagos</option>
            {pagos.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </Select>
        </div>
        {conPago.length === 0 ? (
          <EmptyHint text='Aún no hay pagos registrados. Usa "Marcar como pagado" en cada pago (o registra recibos de meses anteriores) y aquí verás cómo cambian mes a mes.' />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={datos} margin={{ top: 8, right: 6, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={BORDER} vertical={false} />
                <XAxis dataKey="etiqueta" tick={{ fill: TEXT_MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis hide={discreto} tick={{ fill: TEXT_MUTED, fontSize: 10 }} axisLine={false} tickLine={false} width={46} tickFormatter={(v) => "$" + Number(v).toLocaleString("es-MX")} />
                <Tooltip formatter={(v) => fmtMoney(v, discreto)} labelFormatter={(l, p) => p?.[0]?.payload?.mes || l} contentStyle={{ background: PANEL_2, border: `1px solid ${BORDER}`, borderRadius: 8, color: "#F4EFE6", fontSize: 12 }} cursor={{ fill: "rgba(201,161,90,0.08)" }} />
                <Bar dataKey="total" fill={GOLD} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 12, fontSize: 12 }}>
              <div><div style={{ color: TEXT_MUTED }}>Promedio por mes</div><div style={{ fontWeight: 700 }}>{fmtMoney(promedio, discreto)}</div></div>
              <div><div style={{ color: TEXT_MUTED }}>Total en los 12 meses</div><div style={{ fontWeight: 700 }}>{fmtMoney(totalAnio, discreto)}</div></div>
              <div><div style={{ color: TEXT_MUTED }}>Mes más caro</div><div style={{ fontWeight: 700 }}>{caro ? `${caro.etiqueta} · ${fmtMoney(caro.total, discreto)}` : "—"}</div></div>
              <div><div style={{ color: TEXT_MUTED }}>Mes más barato</div><div style={{ fontWeight: 700 }}>{barato ? `${barato.etiqueta} · ${fmtMoney(barato.total, discreto)}` : "—"}</div></div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/* ============================== PAGOS PROGRAMADOS ============================== */

/* ============================== PRÉSTAMOS OTORGADOS ============================== */

function computeHistorialPrestamo(prestamoId, movimientos) {
  return movimientos
    .filter((m) => m.tipo === "cobro_prestamo" && m.prestamoId === prestamoId)
    .sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
}

function PrestamosView({ discreto, prestamos, prestamoPagos, movimientos, cuentas, bolsas, onNuevo, onDelete, onCobrar }) {
  const [verHistorial, setVerHistorial] = useState(null);
  const conSaldo = prestamos.map((p) => ({ ...p, saldo: Math.max(p.importeOriginal - (prestamoPagos[p.id] || 0), 0) }));
  const cuentaMap = Object.fromEntries((cuentas || []).map((c) => [c.id, c.nombre]));
  const bolsaMap = Object.fromEntries((bolsas || []).map((b) => [b.id, b.nombre]));

  return (
    <div>
      <ViewHeader title="Préstamos" subtitle="Dinero que le has prestado a alguien más" onNuevo={onNuevo} nuevoLabel="Declarar préstamo existente" />
      <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 16, fontStyle: "italic" }}>
        Para prestar dinero, usa "Nuevo movimiento" → "Prestar dinero". Aquí verás el historial y los cobros pendientes.
      </div>
      {conSaldo.length === 0 ? <Card><EmptyHint text="No has prestado dinero a nadie todavía." /></Card> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {conSaldo.map((p) => {
            const dias = p.fechaCobroEsperada ? daysUntil(p.fechaCobroEsperada) : null;
            const pagado = p.importeOriginal - p.saldo;
            const progreso = p.importeOriginal > 0 ? (pagado / p.importeOriginal) * 100 : 0;
            const urgencia = p.saldo <= 0 ? GREEN : dias !== null && dias < 0 ? RED : dias !== null && dias <= 5 ? YELLOW : TEXT_MUTED;
            return (
              <Card key={p.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{p.deudor}</div>
                    <div style={{ fontSize: 12, color: urgencia, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                      {p.saldo <= 0 ? <><Check size={12} /> Liquidado</> : dias === null ? "Sin fecha de cobro" : dias < 0 ? <><AlertTriangle size={12} /> Debió pagarte hace {Math.abs(dias)} días</> : `Esperas cobro en ${dias} días`}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => setVerHistorial(p)} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}><History size={15} /></button>
                    <button onClick={() => onDelete(p.id)} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}><Trash2 size={15} /></button>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 12 }}>
                  <Money value={p.saldo} discreto={discreto} size="lg" tone={p.saldo > 0 ? GOLD_LIGHT : GREEN} />
                  <span style={{ fontSize: 11, color: TEXT_MUTED }}>de {fmtMoney(p.importeOriginal, discreto)}</span>
                </div>
                <div style={{ height: 6, background: PANEL_2, borderRadius: 6, overflow: "hidden", marginTop: 8 }}>
                  <div style={{ height: "100%", width: `${progreso}%`, background: GOLD }} />
                </div>
                {p.saldo > 0 && (
                  <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: TEXT_MUTED }}>{p.fechaCobroEsperada ? `Próximo cobro esperado: ${p.fechaCobroEsperada}` : "Sin fecha de cobro"}</span>
                    <button onClick={() => onCobrar(p)} style={BTN_SMALL}>Registrar cobro</button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {verHistorial && (
        <Modal title={`Historial de cobros · ${verHistorial.deudor}`} onClose={() => setVerHistorial(null)}>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 12 }}>
            Saldo pendiente: <Money value={Math.max(verHistorial.importeOriginal - (prestamoPagos[verHistorial.id] || 0), 0)} discreto={discreto} size="sm" tone={GOLD_LIGHT} />
          </div>
          {(() => {
            const hist = computeHistorialPrestamo(verHistorial.id, movimientos);
            if (hist.length === 0) return <EmptyHint text="Todavía no te ha pagado nada." />;
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {hist.map((m) => (
                  <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${BORDER}`, paddingBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{m.fecha}</div>
                      <div style={{ fontSize: 11, color: TEXT_MUTED }}>{cuentaMap[m.cuentaId] || ""} · {bolsaMap[m.bolsaId] || ""}</div>
                    </div>
                    <Money value={m.importe} discreto={discreto} size="sm" tone={GREEN} />
                  </div>
                ))}
              </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
}

function PrestamoModal({ onClose, onSave }) {
  const [deudor, setDeudor] = useState("");
  const [importeOriginal, setImporteOriginal] = useState("");
  const [fechaCobroEsperada, setFechaCobroEsperada] = useState("");
  const [fecha, setFecha] = useState(todayISO());

  const guardar = () => {
    if (!deudor.trim() || !importeOriginal) return;
    onSave({ deudor: deudor.trim(), importeOriginal: parseFloat(importeOriginal) || 0, fecha: fecha || todayISO(), fechaCobroEsperada, estado: "activo" });
  };

  return (
    <Modal title="Registrar préstamo ya existente" onClose={onClose}>
      <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 14, fontStyle: "italic" }}>
        Usa esto solo para declarar un préstamo que ya hiciste antes de usar la app. Si vas a prestar dinero ahora, mejor usa "Nuevo movimiento" → "Prestar dinero", así también se descuenta de tu bolsa.
      </div>
      <Field label="¿Quién te debe?">
        <TextInput placeholder="Ej. Mi hermano" value={deudor} onChange={(e) => setDeudor(e.target.value)} />
      </Field>
      <Field label="Importe prestado">
        <TextInput type="number" placeholder="0" value={importeOriginal} onChange={(e) => setImporteOriginal(e.target.value)} />
      </Field>
      <Field label="Fecha en que lo prestaste">
        <TextInput type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
      </Field>
      <Field label="Fecha esperada de cobro (opcional)">
        <TextInput type="date" value={fechaCobroEsperada} onChange={(e) => setFechaCobroEsperada(e.target.value)} />
      </Field>
      <PrimaryButton onClick={guardar} disabled={!deudor.trim() || !importeOriginal}>Guardar préstamo</PrimaryButton>
    </Modal>
  );
}

function PagosProgramadosView({ discreto, pagos, movimientos = [], onNuevo, onDelete, onPagar }) {
  return (
    <div>
      <ViewHeader title="Pagos Programados" subtitle="Para que nunca se te olvide un pago" onNuevo={onNuevo} nuevoLabel="Nuevo pago" />
      {pagos.length === 0 ? <Card><EmptyHint text="Programa pagos recurrentes (renta, suscripciones, colegiaturas) y te avisaremos cuando se acerquen." /></Card> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {pagos.map((p) => {
            const urgencia = p.dias < 0 ? RED : p.dias <= 3 ? YELLOW : TEXT_MUTED;
            return (
              <Card key={p.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <IconCircle color={GOLD} size={36}><CalendarClock size={17} color={GOLD} /></IconCircle>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{p.nombre}</div>
                      <div style={{ fontSize: 11, color: TEXT_MUTED }}>{p.frecuencia} · <span style={{ color: urgencia }}>{p.dias < 0 ? `venció hace ${Math.abs(p.dias)}d` : p.dias === 0 ? "hoy" : `en ${p.dias}d`}</span></div>
                    </div>
                  </div>
                  <button onClick={() => onDelete(p.id)} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}><Trash2 size={15} /></button>
                </div>
                <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <Money value={p.ultimoMonto || p.importe} discreto={discreto} size="lg" />
                  <button onClick={() => onPagar(p)} style={BTN_SMALL}>Marcar como pagado</button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <GraficaPagosProgramados pagos={pagos} movimientos={movimientos} discreto={discreto} />
    </div>
  );
}

function PagoProgramadoModal({ cuentas, bolsas, onClose, onSave }) {
  const [nombre, setNombre] = useState("");
  const [importe, setImporte] = useState("");
  const [frecuencia, setFrecuencia] = useState("Mensual");
  const [fechaProxima, setFechaProxima] = useState(todayISO());
  const [cuentaId, setCuentaId] = useState(cuentas[0]?.id || "");
  const [bolsaId, setBolsaId] = useState(bolsas[0]?.id || "");

  const guardar = () => {
    if (!nombre.trim() || !importe) return;
    onSave({ nombre: nombre.trim(), importe: parseFloat(importe) || 0, frecuencia, fechaProxima, cuentaId, bolsaId });
  };

  return (
    <Modal title="Nuevo pago programado" onClose={onClose}>
      <Field label="Nombre">
        <TextInput placeholder="Ej. Renta, Netflix, Colegiatura" value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </Field>
      <Field label="Importe">
        <TextInput type="number" placeholder="0" value={importe} onChange={(e) => setImporte(e.target.value)} />
      </Field>
      <Field label="Frecuencia">
        <Select value={frecuencia} onChange={(e) => setFrecuencia(e.target.value)}>
          {FRECUENCIAS.map((f) => <option key={f} value={f}>{f}</option>)}
        </Select>
      </Field>
      <Field label="Próxima fecha">
        <TextInput type="date" value={fechaProxima} onChange={(e) => setFechaProxima(e.target.value)} />
      </Field>
      <Field label="Cuenta habitual">
        <Select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
          {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </Select>
      </Field>
      <Field label="Bolsa habitual">
        <Select value={bolsaId} onChange={(e) => setBolsaId(e.target.value)}>
          {bolsas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
        </Select>
      </Field>
      <PrimaryButton onClick={guardar} disabled={!nombre.trim() || !importe}>Guardar pago</PrimaryButton>
    </Modal>
  );
}

/* ============================== PRESUPUESTOS ============================== */

function PresupuestosView({ discreto, presupuestos, gastoPorCategoria, onNuevo, onDelete, cols2 }) {
  const gastoMap = Object.fromEntries(gastoPorCategoria.map((g) => [g.categoria, g.total]));
  return (
    <div>
      <ViewHeader title="Presupuestos" subtitle="Límites de gasto por categoría del mes en curso — no es dinero apartado, solo un tope" onNuevo={onNuevo} nuevoLabel="Nuevo presupuesto" />
      {presupuestos.length === 0 ? <Card><EmptyHint text="Define un límite por categoría, como Restaurantes o Gasolina." /></Card> : (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols2},1fr)`, gap: 12 }}>
          {presupuestos.map((p) => {
            const gastado = gastoMap[p.categoria] || 0;
            const pct = p.limite > 0 ? Math.min((gastado / p.limite) * 100, 100) : 0;
            const color = pct >= 100 ? RED : pct >= 75 ? YELLOW : GREEN;
            return (
              <Card key={p.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{p.categoria}</div>
                  <button onClick={() => onDelete(p.id)} style={{ background: "none", border: "none", color: TEXT_MUTED, cursor: "pointer" }}><Trash2 size={15} /></button>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <Money value={gastado} discreto={discreto} size="base" tone={color} />
                  <span style={{ fontSize: 11, color: TEXT_MUTED }}>de {fmtMoney(p.limite, discreto)}</span>
                </div>
                <div style={{ height: 6, background: PANEL_2, borderRadius: 6, overflow: "hidden", marginTop: 8 }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: color }} />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PresupuestoModal({ categoriasUsadas, onClose, onSave }) {
  const [categoria, setCategoria] = useState("");
  const [limite, setLimite] = useState("");
  return (
    <Modal title="Nuevo presupuesto" onClose={onClose}>
      <Field label="Categoría">
        <DataListInput options={uniq([...CATEGORIAS_GASTO, ...categoriasUsadas])} placeholder="Ej. Restaurantes, Gasolina" value={categoria} onChange={(e) => setCategoria(e.target.value)} />
      </Field>
      <Field label="Límite mensual">
        <TextInput type="number" placeholder="0" value={limite} onChange={(e) => setLimite(e.target.value)} />
      </Field>
      <PrimaryButton onClick={() => categoria.trim() && limite && onSave({ categoria: categoria.trim(), limite: parseFloat(limite) || 0 })} disabled={!categoria.trim() || !limite}>Guardar presupuesto</PrimaryButton>
    </Modal>
  );
}

/* ============================== PATRIMONIO ============================== */

function PatrimonioView({ discreto, patrimonio, totalBolsas, totalCuentas, totalDeuda, totalPrestado, totalRendimientos, diferenciaAuditoria, bolsas, bolsaSaldos, cuentas, cuentaSaldos, deudas, deudaPagos, prestamos, prestamoPagos, historia, cols2 }) {
  const auditoriaOk = Math.abs(diferenciaAuditoria) < 1;
  return (
    <div>
      <ViewHeader title="Patrimonio" subtitle="Tu riqueza financiera real, calculada automáticamente" />
      <Card style={{ marginBottom: 14, textAlign: "center", padding: 28 }}>
        <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>Patrimonio neto</div>
        <Money value={patrimonio} discreto={discreto} size="xl" tone={patrimonio < 0 ? RED : GOLD_LIGHT} />
        <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 6 }}>
          Bolsas ({fmtMoney(totalBolsas, discreto)}) − Deudas ({fmtMoney(totalDeuda, discreto)}) + Por cobrar ({fmtMoney(totalPrestado, discreto)})
        </div>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 6 }}>
          <ShieldCheck size={15} /> Auditoría de saldos
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 13, color: TEXT_MUTED }}>Total en tus cuentas (bancos)</span>
          <Money value={totalCuentas} discreto={discreto} size="base" />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 13, color: TEXT_MUTED }}>Total en tus bolsas (efectivo)</span>
          <Money value={totalBolsas - totalRendimientos} discreto={discreto} size="base" />
        </div>
        {totalRendimientos > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 13, color: TEXT_MUTED }}>Rendimientos de inversión (aún no en banco)</span>
            <Money value={totalRendimientos} discreto={discreto} size="base" tone={GOLD_LIGHT} />
          </div>
        )}
        <div style={{ height: 1, background: BORDER, margin: "10px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, color: auditoriaOk ? GREEN : RED }}>
            {auditoriaOk ? <Check size={15} /> : <AlertTriangle size={15} />}
            {auditoriaOk ? "Todo cuadra — cuentas y bolsas coinciden" : "Hay una diferencia sin explicar"}
          </span>
          {!auditoriaOk && <Money value={diferenciaAuditoria} discreto={discreto} size="base" tone={RED} />}
        </div>
        {!auditoriaOk && (
          <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 8, fontStyle: "italic" }}>
            Si el saldo real de algún banco no coincide con esto, usa "Nuevo movimiento" → "Ajuste / conciliación" para corregirlo.
          </div>
        )}
      </Card>

      {historia.length >= 2 && (
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Evolución</div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historia}>
                <CartesianGrid stroke={BORDER} vertical={false} />
                <XAxis dataKey="fecha" tick={{ fill: TEXT_MUTED, fontSize: 10 }} tickFormatter={(v) => v.slice(5)} axisLine={{ stroke: BORDER }} tickLine={false} />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: PANEL_2, border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 12 }} formatter={(v) => [fmtMoney(v, discreto), "Patrimonio"]} labelStyle={{ color: TEXT_MUTED }} />
                <Line type="monotone" dataKey="valor" stroke={GOLD} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols2},1fr)`, gap: 14 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Saldo por cuenta</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {cuentas.map((c) => (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: TEXT_MUTED }}>{c.nombre}</span>
                <Money value={cuentaSaldos[c.id] || 0} discreto={discreto} size="sm" />
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Activos por bolsa</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {bolsas.map((b) => (
              <div key={b.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: TEXT_MUTED, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: b.color, display: "inline-block" }} /> {b.nombre}
                </span>
                <Money value={bolsaSaldos[b.id] || 0} discreto={discreto} size="sm" />
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Deudas activas</div>
          {deudas.length === 0 ? <EmptyHint text="Sin deudas activas." /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {deudas.map((d) => (
                <div key={d.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: TEXT_MUTED }}>{d.acreedor}</span>
                  <Money value={Math.max(d.importeOriginal - (deudaPagos[d.id] || 0), 0)} discreto={discreto} size="sm" tone={RED} />
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>Por cobrar (préstamos)</div>
          {prestamos.length === 0 ? <EmptyHint text="No le has prestado dinero a nadie." /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {prestamos.map((p) => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: TEXT_MUTED }}>{p.deudor}</span>
                  <Money value={Math.max(p.importeOriginal - (prestamoPagos[p.id] || 0), 0)} discreto={discreto} size="sm" tone={GOLD_LIGHT} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ============================== REPORTES ============================== */

function ReportesView({ discreto, disponible, totalApartado, totalDeuda, resumenMensual, gastoPorCategoria, historia, cols2 }) {
  const pctAhorro = resumenMensual.ingresos > 0 ? ((resumenMensual.ahorro / resumenMensual.ingresos) * 100).toFixed(0) : 0;
  const totalGastoCategorias = gastoPorCategoria.reduce((a, g) => a + g.total, 0);

  return (
    <div>
      <ViewHeader title="Reportes" subtitle="Preguntas que importan, respondidas simple" />
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols2},1fr)`, gap: 12, marginBottom: 14 }}>
        <Card>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 6 }}>¿Cuánto puedo usar hoy?</div>
          <Money value={disponible} discreto={discreto} size="lg" tone={GREEN} />
        </Card>
        <Card>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 6 }}>¿Cuánto dinero está comprometido?</div>
          <Money value={totalApartado + totalDeuda} discreto={discreto} size="lg" tone={YELLOW} />
        </Card>
        <Card>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 6 }}>¿Cuánto ahorro realmente este mes?</div>
          <Money value={resumenMensual.ahorro} discreto={discreto} size="lg" tone={GOLD_LIGHT} />
          <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 4 }}>{pctAhorro}% de tus ingresos del mes</div>
        </Card>
        <Card>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 6 }}>Ingresos vs. gastos este mes</div>
          <div style={{ display: "flex", gap: 14 }}>
            <Money value={resumenMensual.ingresos} discreto={discreto} size="base" tone={GREEN} />
            <span style={{ color: TEXT_MUTED }}>/</span>
            <Money value={resumenMensual.gastos} discreto={discreto} size="base" tone={RED} />
          </div>
        </Card>
      </div>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>¿En qué gasto más?</div>
        {gastoPorCategoria.length === 0 ? <EmptyHint text="Registra gastos con categoría para ver este reporte." /> : (
          <table className="rep">
            <thead><tr><th>Categoría</th><th style={{ textAlign: "right" }}>Total</th><th style={{ textAlign: "right" }}>% del gasto</th></tr></thead>
            <tbody>
              {gastoPorCategoria.map((g) => (
                <tr key={g.categoria}>
                  <td>{g.categoria}</td>
                  <td style={{ textAlign: "right" }}>{fmtMoney(g.total, discreto)}</td>
                  <td style={{ textAlign: "right", color: TEXT_MUTED }}>{totalGastoCategorias > 0 ? ((g.total / totalGastoCategorias) * 100).toFixed(0) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/* ============================== CONFIGURACIÓN ============================== */

function ConfigView({ bolsas, onUpdatePct, config, onUpdateConfig, onAsignarADisponible, onCrearBolsaFaltante }) {
  const total = bolsas.reduce((acc, b) => acc + (b.porcentaje || 0), 0);
  return (
    <div>
      <ViewHeader title="Configuración" subtitle="Reglas generales de PP Cacao" />

      <Card style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>Perfil</div>
        <Field label="¿Cómo te llamas? (para el saludo del inicio)">
          <TextInput placeholder="Ej. Fernando" value={config.nombreUsuario || ""} onChange={(e) => onUpdateConfig({ nombreUsuario: e.target.value })} />
        </Field>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>Distribución automática de ingresos</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {bolsas.map((b) => (
            <div key={b.id}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: b.color, display: "inline-block" }} /> {b.nombre}
                </span>
                <span style={{ fontWeight: 700 }}>{b.porcentaje || 0}%</span>
              </div>
              <input type="range" min="0" max="100" value={b.porcentaje || 0} onChange={(e) => onUpdatePct(b.id, parseInt(e.target.value))} style={{ width: "100%" }} />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, fontSize: 13, fontWeight: 700, color: total === 100 ? GREEN : YELLOW, display: "flex", alignItems: "center", gap: 6 }}>
          {total !== 100 && <AlertTriangle size={14} />}
          Total: {total}% {total !== 100 && "— procura que sume 100%"}
        </div>
        {total < 100 && (
          <div style={{ marginTop: 10, background: `${YELLOW}15`, border: `1px solid ${YELLOW}55`, borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
              Te falta <b>{100 - total}%</b>. Mientras no lo asignes, ese {100 - total}% de cada ingreso se va automáticamente a <b>Disponible</b>. ¿Qué prefieres?
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={onAsignarADisponible} style={BTN_GHOST}>Dejarlo en Disponible</button>
              <button onClick={() => onCrearBolsaFaltante(100 - total)} style={BTN_SMALL}>Crear una bolsa con ese %</button>
            </div>
          </div>
        )}
        {total > 100 && (
          <div style={{ marginTop: 10, background: `${RED}15`, border: `1px solid ${RED}55`, borderRadius: 10, padding: 12, fontSize: 13, lineHeight: 1.5 }}>
            Te pasaste <b>{total - 100}%</b>. Baja algún porcentaje: mientras pase de 100% no podrás registrar ingresos con reparto automático.
          </div>
        )}
      </Card>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>Privacidad</div>
        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, cursor: "pointer", marginBottom: 12 }}>
          <input type="checkbox" checked={config.discreto} onChange={(e) => onUpdateConfig({ discreto: e.target.checked })} />
          Iniciar siempre en modo discreto (montos ocultos)
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={config.vistaCompacta} onChange={(e) => onUpdateConfig({ vistaCompacta: e.target.checked })} />
          Iniciar en modo privado (solo Disponible visible)
        </label>
      </Card>
    </div>
  );
}

/* ============================== RAÍZ: SESIÓN ============================== */

function LoadingScreen() {
  return (
    <div style={{ background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: TEXT_MUTED, fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      Cargando…
    </div>
  );
}

export default function AppRoot() {
  const [user, setUser] = useState(undefined); // undefined = cargando, null = sin sesión

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return unsub;
  }, []);

  if (user === undefined) return <LoadingScreen />;
  if (!user) return <AuthScreen logoDataUri={LOGO_LIGHT_DATA_URI} />;
  return <PPCacaoApp uid={user.uid} userEmail={user.email} />;
}
